// © 2025 Kaitlyn Troxell
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query,
  where,
  Timestamp,
  setDoc,
  getDoc,
  getDocs,
  orderBy,
  deleteField
} from 'firebase/firestore';
import { ArrowLeft, Ghost, Save, Plus, Minus, Trash2, Calculator, Download, AlertTriangle, TrendingUp, Home, Wrench, DollarSign, X, ClipboardPlus } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import PageHeader from './PageHeader';
import CabinetCalculator from './CabinetPricingCalculator';
import OveragesAnalysis from './OveragesAnalysis';

// Categories matching the Excel template
const CATEGORIES = {
  'Site Prep': {
    items: ['Extra Dirt', 'Permits', 'Blue Prints', 'Dumpster', 'Port-A-Potty', 'Site Plan', 'Soil Borings']
  },
  'Excavation': {
    items: ['Excavate', 'Temp Driveway', 'Final Grade', 'Lot Clearing', 'Sewer Tap', 'Water Tap']
  },
  'Utilities': {
    items: ['Septic', 'Well']
  },
  'Footings and Foundation': {
    items: ['Footings and Foundation'],
    pricePerSqFt: true
  },
  'Foundation': {
    items: ['Waterproof/Damp Proof', 'GMX-10 year waterproofing', 
            'Rub-R-Wall waterproofing - Lifetime', 'Drain Tile Interior', 'Drain Tile Exterior',
            'Window Tile Per Window Well- Egress', 'Steel Beams', 'Window Wells']
  },
  'Lumber Package': {
    items: ['Big C Lumber Package Total', 'Framing Package', 'Basement Framing', 'Trusses',
            'Roofing Materials', 'Windows -Patio Door(s)', 'Exterior Service Door(s)',
            'Big C Install Labor', 'Siding Material', 'Drywall', 'Trim & Interior Doors Mat Cost', 'Deck'],
    bigCPricePerSqFt: true
  },
  'Concrete': {
    items: ['Concrete Estimate','Slab', 'Garage Floor', 'Basement', 'Steps', 'Front Porch', 'Patio',
            'Service Door Pad', 'Driveway', 'Sidewalks', 'Misc Concrete Expenses']
  },
  'Framing': {
    items: ['Quote','Framing Labor', 'Basement Framing', 'Crane', 'Roofing Labor', 'Metal Roofing'],
    framingPricePerSqFt: true
  },
  'Garage': {
    items: ['Garage Door', 'Opener']
  },
  'Siding': {
    items: ['Install - Materials/labor', 'Labor', 'Front Porch Posts', 'Gable Ends']
  },
  'Gutters': {
    items: ['Gutters Estimate', 'Gutters 5"', 'Gutters 6"']
  },
  'Outdoor': {
    items: ['Decks - Patio Estimate', 'Decking Labor', 'Concrete Patio', 'Hardscape', 'Stone', 'Brick']
  },
  'Appliances': {
    items: ['Appliances Estimate', 'Fridge', 'Stove', 'Microwave', 'Dishwasher', 'Hood Vent', 'Wall Oven',
            'Center Grate', 'Washer', 'Dryer', 'Hookups @ $15.00', 'Labor to install RVCG']
  },
  'Plumbing': {
    items: ['Plumbing Estimate','Plumbing Fixtures', 'Plumbing Rough', 'Plumbing Finish']
  },
  'Electrical': {
    items: ['Electrical Estimate', 'AV Installation', 'Electric Fixtures', 'Electric Rough In', 'Electric Finish'],
    electricalQuote: true
  },
  'HVAC': {
    items: ['HVAC Estimate','HVAC Rough', 'HVAC Final', 'Bath Fan Venting', '2 Stage Upgrade',
            '2nd Furnace Upcharge', 'AC Unit Upcharge']
  },
  'Insulation': {
    items: ['Insulation Estimate','Garage Wall Insulation', 'R30 Garage Ceiling Insulation']
  },
  'Drywall': {
    items: ['Drywall Estimate', 'Hang Drywall', 'Finish Drywall']
  },
  'Paint': {
    items: ['Paint Estimate', 'Paint Material', 'Paint Labor']
  },
  'Flooring': {
    items: ['Flooring Estimate', 'Vinyl Costs', 'Carpet Costs', 'Vinyl Labor', 'Carpet Labor', 'Wood', 'Laminate',
            'Tiled Backsplash', 'Tile Material', 'Tile Labor', 'Tiled Shower', 'Tiled Surround',
            'Shower Glass Door']
  },
  'Fireplace': {
    items: ['Fireplace  Estimate', 'Fireplace Unit Rough', 'Fireplace Start Up']
  },
  'Cabinetry': {
    items: ['Cabinetry Estimate', 'Kitchen/Vanities Cabinets White', 'Kitchen/Vanities Cabinets Colored',
            '4-8\' Bench', '8\' or Longer Bench', '5" Cabinet Door Pulls', '7" Cabinet Door Pulls',
            '10" Cabinet Door Pulls', '3D Prints of Cabinet Layout', 'Countertop Entry Level',
            'Countertop Group A', 'Countertop Group B', 'Countertop OTHER', 'Kitchen Sink UM',
            'Kitchen Sink Upgrade', 'Kitchen Sink Upgrade (Kraus)', 'Vanity Sink Rectangle',
            'Vanity Sink Oval', 'Laundry', 'Bar']
  },
  'Millwork': {
    items: ['Millwork Estimate', 'Trim Labor', 'Locking Door Handle Interior', 'Hall/Closet Door Handle Interior',
            'Pocket Door Handle', 'Exterior Lock Sets', 'Dummy Knobs', 'Mirror / Brackets',
            'Closet Shelving']
  },
  'Other': {
    items: ['Utilities', 'Insurance', 'Grass', 'Landscaping']
  },
  'Misc Expenses': {
    items: [],
    allowCustomItems: true
  },
  'Final': {
    items: ['Cleanings - Final Clean', 'Realtor Fee', 'Staging - Flourish', 'Add Ons', 'Employee Expenses']
  }
};

const CABINET_CATEGORIES = {
  'Base Cabinets': [
    { model: 'SB24DD', actualPrice: 990, rvcgPrice: 585, pulls: 2, pullSize: '7"', sqftMultiplier: 4.2 },
    { model: 'SB27', actualPrice: 1000, rvcgPrice: 595, pulls: 2, pullSize: '7"', sqftMultiplier: 4.7 },
    { model: 'SB30', actualPrice: 1033, rvcgPrice: 628, pulls: 2, pullSize: '7"', sqftMultiplier: 5.21 },
    { model: 'SB33', actualPrice: 1061, rvcgPrice: 656, pulls: 2, pullSize: '7"', sqftMultiplier: 5.73 },
    { model: 'SB36', actualPrice: 1079, rvcgPrice: 674, pulls: 2, pullSize: '7"', sqftMultiplier: 6.25 },
    { model: 'SB39', actualPrice: 1262, rvcgPrice: 695, pulls: 2, pullSize: '7"', sqftMultiplier: 6.77 },
    { model: 'SB42', actualPrice: 1355, rvcgPrice: 788, pulls: 2, pullSize: '7"', sqftMultiplier: 7.3 },
    { model: 'FSB36', actualPrice: 1418, rvcgPrice: 1013, pulls: 2, pullSize: '7"', sqftMultiplier: 6.25 },
    { model: 'SS36', actualPrice: 1906, rvcgPrice: 1501, pulls: 1, pullSize: '7"', sqftMultiplier: 8.16 },
    { model: 'LS36', actualPrice: 1505, rvcgPrice: 1100, pulls: 1, pullSize: '7"', sqftMultiplier: 8.16 },
    { model: 'BER33', actualPrice: 1240, rvcgPrice: 835, pulls: 1, pullSize: '7"', sqftMultiplier: 7.12 },
    { model: 'BER36', actualPrice: 1255, rvcgPrice: 850, pulls: 1, pullSize: '7"', sqftMultiplier: 8.16 },
  ],
  'Base Microwave & Wine': [
    { model: 'BMW30', actualPrice: 1435, rvcgPrice: 1030, pulls: 1, pullSize: '10"', sqftMultiplier: 5.21 },
    { model: 'BWB18', actualPrice: 1583, rvcgPrice: 1239, pulls: 2, pullSize: '7"', sqftMultiplier: 3.18 },
  ],
  'Regular Base': [
    { model: 'B36', actualPrice: 1468, rvcgPrice: 1063, pulls: 3, pullSize: '10"', sqftMultiplier: 6.25 },
    { model: 'B33', actualPrice: 1322, rvcgPrice: 917, pulls: 3, pullSize: '10"', sqftMultiplier: 5.73 },
    { model: 'B30', actualPrice: 1276, rvcgPrice: 871, pulls: 3, pullSize: '7"', sqftMultiplier: 5.21 },
    { model: 'B27', actualPrice: 1227, rvcgPrice: 822, pulls: 3, pullSize: '7"', sqftMultiplier: 4.7 },
    { model: 'B24DD', actualPrice: 1214, rvcgPrice: 809, pulls: 3, pullSize: '7"', sqftMultiplier: 4.2 },
    { model: 'B21', actualPrice: 1030, rvcgPrice: 686, pulls: 2, pullSize: '7"', sqftMultiplier: 3.72 },
    { model: 'B18', actualPrice: 1000, rvcgPrice: 656, pulls: 2, pullSize: '7"', sqftMultiplier: 3.18 },
    { model: 'B15', actualPrice: 955, rvcgPrice: 611, pulls: 2, pullSize: '7"', sqftMultiplier: 2.66 },
    { model: 'B12', actualPrice: 899, rvcgPrice: 555, pulls: 2, pullSize: '7"', sqftMultiplier: 2.13 },
    { model: 'B09', actualPrice: 863, rvcgPrice: 519, pulls: 2, pullSize: '7"', sqftMultiplier: 1.6 },
  ],
  'Base Drawer': [
    { model: 'B36D3', actualPrice: 1820, rvcgPrice: 1415, pulls: 3, pullSize: '10"', sqftMultiplier: 6.375 },
    { model: 'B30D3', actualPrice: 1743, rvcgPrice: 1338, pulls: 3, pullSize: '7"', sqftMultiplier: 5.21 },
    { model: 'B27D3', actualPrice: 1416, rvcgPrice: 1416, pulls: 3, pullSize: '7"', sqftMultiplier: 4.7 },
    { model: 'B24D3', actualPrice: 1214, rvcgPrice: 1146, pulls: 3, pullSize: '7"', sqftMultiplier: 4.2 },
    { model: 'B21D3', actualPrice: 1427, rvcgPrice: 1083, pulls: 3, pullSize: '7"', sqftMultiplier: 3.72 },
    { model: 'B18D3', actualPrice: 1363, rvcgPrice: 1019, pulls: 3, pullSize: '7"', sqftMultiplier: 3.18 },
    { model: 'B15D3', actualPrice: 1296, rvcgPrice: 952, pulls: 3, pullSize: '7"', sqftMultiplier: 2.66 },
    { model: 'B12D3', actualPrice: 1230, rvcgPrice: 886, pulls: 3, pullSize: '7"', sqftMultiplier: 2.13 },
  ],
  'Utility': [
    { model: 'U249624', actualPrice: 2321, rvcgPrice: 1754, pulls: 2, pullSize: '10"', sqftMultiplier: 0 },
    { model: 'OCD339624', actualPrice: 3489, rvcgPrice: 2555, pulls: 2, pullSize: '10"', sqftMultiplier: 0 },
  ],
  'Wall Cabinets': [
    { model: 'W0942', actualPrice: 669, rvcgPrice: 325, pulls: 1, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W1242', actualPrice: 733, rvcgPrice: 389, pulls: 1, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W1542', actualPrice: 770, rvcgPrice: 426, pulls: 1, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W1842', actualPrice: 817, rvcgPrice: 473, pulls: 1, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W2142', actualPrice: 852, rvcgPrice: 508, pulls: 1, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W2442DD', actualPrice: 816, rvcgPrice: 612, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W2742', actualPrice: 1033, rvcgPrice: 628, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3042', actualPrice: 1079, rvcgPrice: 674, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3342', actualPrice: 1116, rvcgPrice: 711, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3642', actualPrice: 1173, rvcgPrice: 768, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W4242', actualPrice: 1369, rvcgPrice: 886, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'RW3318', actualPrice: 817, rvcgPrice: 473, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'RW3324', actualPrice: 995, rvcgPrice: 651, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'RW3612', actualPrice: 828, rvcgPrice: 484, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'RW3618', actualPrice: 835, rvcgPrice: 473, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'RW3624', actualPrice: 995, rvcgPrice: 651, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'WER2442', actualPrice: 1294, rvcgPrice: 889, pulls: 1, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3018', actualPrice: 694, rvcgPrice: 318, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3024', actualPrice: 908, rvcgPrice: 564, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3615', actualPrice: 694, rvcgPrice: 350, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3618', actualPrice: 733, rvcgPrice: 389, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
    { model: 'W3624', actualPrice: 1004, rvcgPrice: 660, pulls: 2, pullSize: '7"', sqftMultiplier: 0 },
  ],
  'Vanities': [
    // Single vanities (no sink auto-calculation)
    { model: 'VSB24DD', actualPrice: 925, rvcgPrice: 520, pulls: 2, pullSize: '7"', sqftMultiplier: 3.75, vanitySink: false },
    { model: 'VSB27', actualPrice: 941, rvcgPrice: 536, pulls: 2, pullSize: '7"', sqftMultiplier: 4.23, vanitySink: false },
    { model: 'VSB30', actualPrice: 980, rvcgPrice: 575, pulls: 2, pullSize: '7"', sqftMultiplier: 4.7, vanitySink: false },
    { model: 'VSB33', actualPrice: 1000, rvcgPrice: 595, pulls: 2, pullSize: '7"', sqftMultiplier: 5.16, vanitySink: false },
    { model: 'VSB36', actualPrice: 1024, rvcgPrice: 619, pulls: 2, pullSize: '7"', sqftMultiplier: 5.63, vanitySink: false },
    { model: 'VSB48', actualPrice: 1308, rvcgPrice: 741, pulls: 4, pullSize: '7"', sqftMultiplier: 7.5, vanitySink: false },
    { model: 'VSB60DB4', actualPrice: 3137, rvcgPrice: 2107, pulls: 7, pullSize: '7"', sqftMultiplier: 9.375, vanitySink: false },
    
    // DOUBLE vanities - these AUTO-COUNT as rectangle sinks
    { model: 'VSDB36', actualPrice: 1711, rvcgPrice: 919, pulls: 5, pullSize: '7"', sqftMultiplier: 5.63, vanitySink: true },
    { model: 'VSDB42', actualPrice: 1872, rvcgPrice: 1080, pulls: 5, pullSize: '7"', sqftMultiplier: 6.56, vanitySink: true },
    { model: 'VSDB48', actualPrice: 1934, rvcgPrice: 1142, pulls: 5, pullSize: '7"', sqftMultiplier: 7.5, vanitySink: true },
    
    // Other vanity types
    { model: 'VBL30', actualPrice: 1804, rvcgPrice: 1399, pulls: 5, pullSize: '7"', sqftMultiplier: 4.69, vanitySink: false },
    { model: 'VBR30', actualPrice: 1804, rvcgPrice: 1399, pulls: 5, pullSize: '7"', sqftMultiplier: 4.69, vanitySink: false },
    { model: 'VBL36', actualPrice: 2230, rvcgPrice: 1438, pulls: 5, pullSize: '7"', sqftMultiplier: 5.63, vanitySink: false },
    { model: 'VBR36', actualPrice: 2230, rvcgPrice: 1438, pulls: 5, pullSize: '7"', sqftMultiplier: 5.63, vanitySink: false },
    { model: 'VBL42', actualPrice: 2284, rvcgPrice: 1492, pulls: 5, pullSize: '7"', sqftMultiplier: 6.56, vanitySink: false },
    { model: 'VBR42', actualPrice: 2284, rvcgPrice: 1492, pulls: 5, pullSize: '7"', sqftMultiplier: 6.56, vanitySink: false },
    { model: 'VB12', actualPrice: 875, rvcgPrice: 531, pulls: 2, pullSize: '7"', sqftMultiplier: 1.875, vanitySink: false },
    { model: 'VB15', actualPrice: 880, rvcgPrice: 536, pulls: 2, pullSize: '7"', sqftMultiplier: 2.343, vanitySink: false },
    { model: 'VB18', actualPrice: 927, rvcgPrice: 583, pulls: 2, pullSize: '7"', sqftMultiplier: 2.82, vanitySink: false },
    { model: 'VDB12', actualPrice: 1077, rvcgPrice: 733, pulls: 3, pullSize: '7"', sqftMultiplier: 1.875, vanitySink: false },
    { model: 'VDB15', actualPrice: 1141, rvcgPrice: 797, pulls: 3, pullSize: '7"', sqftMultiplier: 2.343, vanitySink: false },
    { model: 'VDB18', actualPrice: 1179, rvcgPrice: 835, pulls: 3, pullSize: '7"', sqftMultiplier: 2.82, vanitySink: false },
    { model: 'LINEN CAB W/ DRAWERS', actualPrice: 2329, rvcgPrice: 1762, pulls: 3, pullSize: '7"', sqftMultiplier: 0, vanitySink: false },
    { model: 'LINEN CAB', actualPrice: 2329, rvcgPrice: 1104, pulls: 2, pullSize: '7"', sqftMultiplier: 0, vanitySink: false },
  ]
};

function SwornStatementsView({ 
  user, 
  userRole, 
  onNavigate, 
  currentProject,
  projects,
  onProjectChange 
}) {
  const [swornData, setSwornData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [contractors, setContractors] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [contractPrice, setContractPrice] = useState(0);
  const [downPayment, setDownPayment] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAddOns, setShowAddOns] = useState(false);
  const [addOns, setAddOns] = useState([]);
  const [squareFeet, setSquareFeet] = useState(2000);
  const [cabinetPulls, setCabinetPulls] = useState(30);
  const [drawerPulls, setDrawerPulls] = useState(20);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showContractorMetrics, setShowContractorMetrics] = useState(false);
  const [profitMarginPercent, setProfitMarginPercent] = useState(25);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [historicalJobs, setHistoricalJobs] = useState([]);
  const [miscExpenses, setMiscExpenses] = useState([]);
  const [selectedDraw, setSelectedDraw] = React.useState(0);
  const [newMiscExpenseName, setNewMiscExpenseName] = useState('');
  const [drawsReceived, setDrawsReceived] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
  const [datesReceived, setDatesReceived] = useState(['', '', '', '', '', '', '', '']);
  const [drawCheckboxes, setDrawCheckboxes] = useState([false, false, false, false, false, false, false, false]);
  const [showConcretePricing, setShowConcretePricing] = useState(false);
  const [showConcreteSQFTPricing, setShowConcreteSQFTPricing] = useState(false);
  const [concreteOptions, setConcreteOptions] = useState([]);
  const [concreteSQFTOptions, setConcreteSQFTOptions] = useState([]);
  const [concreteSQFTInputs, setConcreteSQFTInputs] = useState({});
  const [existingConcreteItems, setExistingConcreteItems] = useState({});
  const [showCabinetPricing, setShowCabinetPricing] = useState(false);
  const [cabinetQuantities, setCabinetQuantities] = useState({});
  const [cabinetPricingType, setCabinetPricingType] = useState('rvcg');
  const [cabinetSearchTerm, setCabinetSearchTerm] = useState('');
  const [showCalculator, setShowCalculator] = useState(false);
  const [showOveragesModal, setShowOveragesModal] = useState(false);
  const [customItems, setCustomItems] = useState({});
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newItemCategory, setNewItemCategory] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [showMiscBreakdown, setShowMiscBreakdown] = useState(false);

  const saveAllConcreteItems = async () => {
    try {
      let savedCount = 0;
      let errorCount = 0;
      
      let miscExpensesTotal = 0;
      const regularItems = [];
      const miscItems = [];
      
      // Create name-based storage
      const namedInputs = {};
      
      for (const option of concreteSQFTOptions) {
        const sqft = parseFloat(concreteSQFTInputs[option.id]) || 0;
        const cost = parseFloat(option.costPerSqft) || 0;
        const totalCost = sqft * cost;
        
        const optionName = option.option || option.name || '';
        
        // Store by name
        if (sqft > 0) {
          namedInputs[optionName] = sqft;
        }
        
        if (sqft === 0) continue;
        
        const optionNameLower = optionName.toLowerCase();
        const miscKeywords = ['tile', 'bob cat', 'bobcat', 'visqueen', 'extra', 'winter', 'calcium'];
        const isMisc = miscKeywords.some(keyword => optionNameLower.includes(keyword));
        
        if (isMisc) {
          miscExpensesTotal += totalCost;
          miscItems.push({
            name: optionName,
            sqft,
            costPerSqft: cost,
            totalCost
          });
        } else {
          regularItems.push({ name: optionName, cost: totalCost });
        }
      }
      
      // Save regular items
      for (const item of regularItems) {
        try {
          await updateConcreteItemCost(item.name, item.cost);
          savedCount++;
        } catch (error) {
          console.error(`Error saving ${item.name}:`, error);
          errorCount++;
        }
      }
      
      // Save misc expenses
      try {
        if (miscExpensesTotal > 0) {
          await updateConcreteItemCost('Misc Concrete Expenses', miscExpensesTotal);
          savedCount++;
        } else {
          await updateConcreteItemCost('Misc Concrete Expenses', 0);
        }
      } catch (error) {
        console.error('Error saving Misc Concrete Expenses:', error);
        errorCount++;
      }
      
      // Save concrete options AND inputs per-project
      try {
        const docRef = doc(db, 'swornStatements', currentProject.id);
        await setDoc(docRef, {
          concreteSQFTOptions: concreteSQFTOptions, // Save options per project
          concreteNamedInputs: namedInputs,
          miscBreakdownData: miscItems,
          updatedAt: Timestamp.now()
        }, { merge: true });
      } catch (error) {
        console.error('Error saving breakdown data:', error);
      }
      
      await loadConcreteOptions();
      
      if (errorCount === 0) {
        alert(`✓ Successfully saved ${savedCount} concrete items!`);
      } else {
        alert(`⚠ Saved ${savedCount} items with ${errorCount} errors.`);
      }

      setShowConcreteSQFTPricing(false);
    } catch (error) {
      console.error('Error saving concrete items:', error);
      alert('❌ Error saving concrete pricing. Please try again.');
    }
  };

  const loadConcreteOptions = async () => {
    try {
      if (!currentProject?.id) return;
      
      // Load from the current project's sworn statement
      const statementRef = doc(db, 'swornStatements', currentProject.id);
      const statementDoc = await getDoc(statementRef);
      
      if (statementDoc.exists()) {
        const data = statementDoc.data();
        
        // Load saved concrete options (or use defaults if none exist)
        const savedOptions = data.concreteSQFTOptions || getDefaultConcreteOptions();
        setConcreteSQFTOptions(savedOptions);
        
        // Load saved SQFT inputs by name
        if (data.concreteNamedInputs) {
          const idBasedInputs = {};
          
          // Match saved names to current option IDs
          savedOptions.forEach(option => {
            const optionName = option.option || option.name || '';
            if (data.concreteNamedInputs[optionName]) {
              idBasedInputs[option.id] = data.concreteNamedInputs[optionName];
            }
          });
          
          setConcreteSQFTInputs(idBasedInputs);
        }
        
        // Load existing concrete items
        const items = data.items || {};
        const concreteItems = {};
        
        Object.keys(items).forEach(key => {
          if (key.toLowerCase().includes('concrete|||')) {
            const itemName = key.split('|||')[1]?.trim();
            const itemData = items[key];
            
            if (itemName && itemData) {
              concreteItems[itemName] = {
                originalBidCost: itemData.originalBidCost || 0,
                key: key
              };
            }
          }
        });
        
        setExistingConcreteItems(concreteItems);
      } else {
        // No sworn statement yet, use defaults
        setConcreteSQFTOptions(getDefaultConcreteOptions());
      }
      
    } catch (error) {
      console.error('Error loading concrete options:', error);
    }
  };

  // Add this helper function to get default concrete options
  const getDefaultConcreteOptions = () => {
    return [
      { id: 'driveway', option: 'Driveway', name: 'Driveway', costPerSqft: '6.10', unit: 'sqft' },
      { id: 'garage', option: 'Garage Floor', name: 'Garage Floor', costPerSqft: '6.25', unit: 'sqft' },
      { id: 'basement', option: 'Basement Floor', name: 'Basement Floor', costPerSqft: '6.15', unit: 'sqft' },
      { id: 'porch', option: 'Porch 1-2" Overhang', name: 'Porch 1-2" Overhang', costPerSqft: '10.65', unit: 'sqft' },
      { id: 'sidewalk', option: 'Sidewalk', name: 'Sidewalk', costPerSqft: '6.85', unit: 'sqft' },
      { id: 'patio', option: 'Patio', name: 'Patio', costPerSqft: '6.25', unit: 'sqft' },
      { id: 'service-door', option: '4x4x Service Door', name: '4x4x Service Door', costPerSqft: '300.00', unit: 'each' },
      { id: 'steps', option: '3ft Steps', name: '3ft Steps', costPerSqft: '300.00', unit: 'each' },
      { id: 'tile', option: 'Tile Under Sidewalk', name: 'Tile Under Sidewalk', costPerSqft: '70.00', unit: 'each' },
      { id: 'bobcat', option: 'East Bob Cat Time', name: 'East Bob Cat Time', costPerSqft: '95.00', unit: 'hour' },
      { id: 'visqueen', option: 'Visqueen', name: 'Visqueen', costPerSqft: '0.75', unit: 'sqft' },
      { id: 'extras', option: 'Extras - Winter Charge, nc, Calcium', name: 'Extras - Winter Charge, nc, Calcium', costPerSqft: '44.00', unit: 'yard' }
    ];
  };

  // load historical data for all projects except the current one
  const loadAllProjectsHistoricalData = async () => {
    if (!projects || projects.length === 0) return;
    
    const loadedJobs = [];
    
    for (const project of projects) {
      // Skip the current project
      if (project.id === currentProject?.id) continue;
      
      try {
        const docRef = doc(db, 'swornStatements', project.id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const items = data.items || {};
          
          // Extract Foundation cost
          const foundationKey = Object.keys(items).find(key => 
            key.includes('Footings and Foundation|||Footings and Foundation')
          );
          const foundationCost = foundationKey ? (items[foundationKey].originalBidCost || 0) : 0;
          
          // Extract Framing cost (Quote)
          const framingKey = Object.keys(items).find(key => 
            key.includes('Framing|||Quote')
          );
          const framingCost = framingKey ? (items[framingKey].originalBidCost || 0) : 0;
          
          // Extract Big C cost
          const bigCKey = Object.keys(items).find(key => 
            key.includes('Lumber Package|||Big C Lumber Package Total')
          );
          const bigCCost = bigCKey ? (items[bigCKey].originalBidCost || 0) : 0;
          
          // Get square feet
          const sqft = data.squareFeet || 0;
          
          // Only add if it has meaningful data
          if (foundationCost > 0 || framingCost > 0 || bigCCost > 0 || sqft > 0) {
            loadedJobs.push({
              id: project.id, // Use project ID as the job ID to prevent duplicates
              projectId: project.id,
              customerName: project.name,
              foundation: foundationCost.toFixed(2),
              framing: framingCost.toFixed(2),
              bigC: bigCCost.toFixed(2),
              sqft: sqft
            });
          }
        }
      } catch (error) {
        console.error(`Error loading project ${project.name}:`, error);
      }
    }
    
    // Merge with any existing manual entries in historicalJobs
    // Keep manual entries (those without projectId) and replace/add project-based ones
    const manualJobs = historicalJobs.filter(job => !job.projectId);
    const mergedJobs = [...manualJobs, ...loadedJobs];
    
    setHistoricalJobs(mergedJobs);
    
    // Save to Firebase
    if (mergedJobs.length > 0) {
      try {
        const docRef = doc(db, 'swornStatements', 'historicalJobs');
        await setDoc(docRef, {
          jobs: mergedJobs,
          updatedAt: Timestamp.now()
        }, { merge: true });
      } catch (error) {
        console.error('Error saving historical jobs:', error);
      }
    }
  };

  // calculate and extract historical data for a given project
  const extractHistoricalDataFromProject = (projectId, projectName) => {
    // Get the sworn statement data for this project
    const docRef = doc(db, 'swornStatements', projectId);
    
    getDoc(docRef).then((docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const items = data.items || {};
        
        // Extract Foundation cost
        const foundationKey = Object.keys(items).find(key => 
          key.includes('Footings and Foundation|||Footings and Foundation')
        );
        const foundationCost = foundationKey ? (items[foundationKey].originalBidCost || 0) : 0;
        
        // Extract Framing cost (Quote)
        const framingKey = Object.keys(items).find(key => 
          key.includes('Framing|||Quote')
        );
        const framingCost = framingKey ? (items[framingKey].originalBidCost || 0) : 0;
        
        // Extract Big C cost
        const bigCKey = Object.keys(items).find(key => 
          key.includes('Lumber Package|||Big C Lumber Package Total')
        );
        const bigCCost = bigCKey ? (items[bigCKey].originalBidCost || 0) : 0;
        
        // Get square feet
        const sqft = data.squareFeet || 0;
        
        // Check if this project already exists in historical jobs
        const existingJobIndex = historicalJobs.findIndex(job => job.projectId === projectId);
        
        if (existingJobIndex >= 0) {
          // Update existing job
          const updatedJobs = [...historicalJobs];
          updatedJobs[existingJobIndex] = {
            ...updatedJobs[existingJobIndex],
            customerName: projectName,
            foundation: foundationCost,
            framing: framingCost,
            bigC: bigCCost,
            sqft: sqft
          };
          setHistoricalJobs(updatedJobs);
        } else {
          // Add new job
          const newJob = {
            id: Date.now(),
            projectId: projectId, // Store project ID to prevent duplicates
            customerName: projectName,
            foundation: foundationCost,
            framing: framingCost,
            bigC: bigCCost,
            sqft: sqft
          };
          setHistoricalJobs([...historicalJobs, newJob]);
        }
        
        // Save to Firebase
        saveHistoricalJobs();
      }
    }).catch((error) => {
      console.error('Error loading project data:', error);
    });
  };

  // Fetch historical jobs (shared across all projects)
  useEffect(() => {
    if (!user) return;

    const historicalJobsRef = doc(db, 'swornStatements', 'historicalJobs');
    
    const unsubscribe = onSnapshot(historicalJobsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setHistoricalJobs(data.jobs || []);
      } else {
        setHistoricalJobs([]);
      }
    }, (error) => {
      console.error('Error fetching historical jobs:', error);
    });

    return () => unsubscribe();
  }, [user]);

  // get historical data when pricing modal is shown
  useEffect(() => {
    if (showPricingModal && projects && projects.length > 0) {
      loadAllProjectsHistoricalData();
    }
  }, [showPricingModal, projects]);

  // Fetch active contractors
  useEffect(() => {
    if (!user) return;

    const contractorsQuery = query(
      collection(db, 'contractors'),
      where('active', '==', true)
    );
    
    const unsubscribe = onSnapshot(contractorsQuery, (snapshot) => {
      const contractorsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContractors(contractorsList);
    }, (error) => {
      console.error('Error fetching contractors:', error);
    });

    return () => unsubscribe();
  }, [user]);

  // Fetch sworn statement data for current project
  useEffect(() => {
    if (!user || !currentProject) {
      // Clear all state when no project is selected
      setSwornData(null);
      setContractPrice(0);
      setDownPayment(0);
      setSquareFeet(0);
      setCabinetPulls(0);
      setDrawerPulls(0);
      setProfitMarginPercent(25);
      setMiscExpenses([]);
      setAddOns([]);
      setDrawsReceived([0, 0, 0, 0, 0, 0, 0, 0]);
      setDatesReceived(['', '', '', '', '', '', '', '']);
      setDrawCheckboxes([false, false, false, false, false, false, false, false]);
      setConcreteSQFTInputs({}); // Clear concrete inputs
      setExistingConcreteItems({}); // Clear existing items
      setLoading(false);
      return;
    }

    // Reset state immediately when project changes
    setContractPrice(0);
    setDownPayment(0);
    setSquareFeet(0);
    setCabinetPulls(0);
    setDrawerPulls(0);
    setProfitMarginPercent(25);
    setMiscExpenses([]);
    setAddOns([]);
    setDrawsReceived([0, 0, 0, 0, 0, 0, 0, 0]);
    setDatesReceived(['', '', '', '', '', '', '', '']);
    setDrawCheckboxes([false, false, false, false, false, false, false, false]);
    setConcreteSQFTInputs({}); // Clear concrete inputs when switching projects
    setExistingConcreteItems({}); // Clear existing items when switching projects
    setLoading(true);

    const docRef = doc(db, 'swornStatements', currentProject.id);
    
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSwornData(data);
        setContractPrice(data.contractPrice || 0);
        setDownPayment(data.downPayment || 0);
        setSquareFeet(data.squareFeet || 0);
        setCabinetPulls(data.cabinetPulls || 0);
        setDrawerPulls(data.drawerPulls || 0);
        setProfitMarginPercent(data.profitMarginPercent || 25);
        setMiscExpenses(data.miscExpenses || []);
        setAddOns(data.addOns || []);
        setDrawsReceived(data.drawsReceived || [0, 0, 0, 0, 0, 0, 0, 0]);
        setDatesReceived(data.datesReceived || ['', '', '', '', '', '', '', '']);
        setDrawCheckboxes(data.drawCheckboxes || [false, false, false, false, false, false, false, false]);
        setCabinetQuantities(data.cabinetQuantities || {});
        setCabinetPricingType(data.cabinetPricingType || 'rvcg');
        setCustomItems(data.customItems || {});
      } else {
        const initialData = {
          items: {},
          contractPrice: 0,
          downPayment: 0,
          squareFeet: 0,
          cabinetPulls: 0,
          drawerPulls: 0,
          profitMarginPercent: 25,
          miscExpenses: [],
          addOns: [],
          drawsReceived: [0, 0, 0, 0, 0, 0, 0, 0],
          datesReceived: ['', '', '', '', '', '', '', ''],
          drawCheckboxes: [false, false, false, false, false, false, false, false],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          customItems: {}
        };
        setSwornData(initialData);
        setMiscExpenses([]);
        setAddOns([]);
        setCustomItems({});
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching sworn statement:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, currentProject]);

  // Fetch concrete options
  useEffect(() => {
    if (currentProject?.id) {
      loadConcreteOptions();
    }
  }, [currentProject?.id]);

  // Load concrete options when modal opens
  useEffect(() => {
    if (showConcreteSQFTPricing && currentProject) {
      loadConcreteOptions();
    }
  }, [showConcreteSQFTPricing]);

  //cabinet pull add-ons
  useEffect(() => {
    if (!currentProject || !swornData) return;
    
    if (swornData.cabinetQuantities) {
      setCabinetQuantities(swornData.cabinetQuantities);
    }
    if (swornData.cabinetPricingType) {
      setCabinetPricingType(swornData.cabinetPricingType);
    }
  }, [swornData, currentProject]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);

  const handleAddOnNameChange = useCallback((e) => {
    setNewAddOnName(e.target.value);
  }, []);

  const addAddOn = async (name) => {
    if (!currentProject || !name) return;
    
    const newAddOn = {
      id: Date.now(),
      name: name,
      price: 0,
      overages: 0,
      amountPaid: 0
    };
    
    const updatedAddOns = [...addOns, newAddOn];
    setAddOns(updatedAddOns);
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        addOns: updatedAddOns,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error adding add-on:', error);
    }
  };

  const updateAddOn = (id, field, value) => {
    // Update local state immediately for responsive UI
    const updatedAddOns = addOns.map(addon => 
      addon.id === id ? { ...addon, [field]: parseFloat(value) || 0 } : addon
    );
    setAddOns(updatedAddOns);
    
    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Set new timeout to save after user stops typing (500ms delay)
    const newTimeout = setTimeout(async () => {
      try {
        const docRef = doc(db, 'swornStatements', currentProject.id);
        await setDoc(docRef, {
          addOns: updatedAddOns,
          updatedAt: Timestamp.now()
        }, { merge: true });
      } catch (error) {
        console.error('Error updating add-on:', error);
      }
    }, 500);
    
    setSaveTimeout(newTimeout);
  };

  const deleteAddOn = async (id) => {
    if (!currentProject) return;
    
    const updatedAddOns = addOns.filter(addon => addon.id !== id);
    setAddOns(updatedAddOns);
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        addOns: updatedAddOns,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error deleting add-on:', error);
    }
  };

  const addCustomItem = async () => {
    if (!currentProject || !newItemCategory || !newItemName.trim()) {
      alert('Please select a category and enter an item name');
      return;
    }
    
    const updatedCustomItems = { ...customItems };
    if (!updatedCustomItems[newItemCategory]) {
      updatedCustomItems[newItemCategory] = [];
    }
    
    updatedCustomItems[newItemCategory].push(newItemName.trim());
    setCustomItems(updatedCustomItems);
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        customItems: updatedCustomItems,
        updatedAt: Timestamp.now()
      }, { merge: true });
      
      // Reset form
      setNewItemName('');
      setNewItemCategory('');
      setShowAddItemModal(false);
    } catch (error) {
      console.error('Error adding custom item:', error);
    }
  };

  const deleteCustomItem = async (category, itemName) => {
    if (!currentProject) return;
    
    const updatedCustomItems = { ...customItems };
    if (updatedCustomItems[category]) {
      updatedCustomItems[category] = updatedCustomItems[category].filter(item => item !== itemName);
      
      // Remove category if empty
      if (updatedCustomItems[category].length === 0) {
        delete updatedCustomItems[category];
      }
    }
    
    setCustomItems(updatedCustomItems);
    
    // Also remove the item data
    const key = getItemKey(category, itemName);
    const updatedItems = { ...swornData.items };
    delete updatedItems[key];
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        customItems: updatedCustomItems,
        items: updatedItems,
        updatedAt: Timestamp.now()
      }, { merge: true });
      
      setSwornData({
        ...swornData,
        items: updatedItems
      });
    } catch (error) {
      console.error('Error deleting custom item:', error);
    }
  };

  const loadSavedQuantities = () => {
    // In real implementation, load from Firebase
    const saved = localStorage.getItem('cabinetQuantities');
    if (saved) {
      setCabinetQuantities(JSON.parse(saved));
    }
  };

  const updateQuantity = (model, quantity) => {
    setCabinetQuantities(prev => ({
      ...prev,
      [model]: parseInt(quantity) || 0
    }));
  };

  const calculateTotals = () => {
    let total7InchPulls = 0;
    let total10InchPulls = 0;
    let totalCost = 0;
    let totalSqft = 0;

    Object.entries(CABINET_CATEGORIES).forEach(([category, cabinets]) => {
      cabinets.forEach(cabinet => {
        const qty = cabinetQuantities[cabinet.model] || 0;
        if (qty > 0) {
          const price = cabinetPricingType === 'actual' ? cabinet.actualPrice : cabinet.rvcgPrice;
          totalCost += price * qty;
          totalSqft += (cabinet.sqftMultiplier || 0) * qty;
          
          const pullCount = cabinet.pulls * qty;
          if (cabinet.pullSize === '7"') {
            total7InchPulls += pullCount;
          } else if (cabinet.pullSize === '10"') {
            total10InchPulls += pullCount;
          }
        }
      });
    });

    return { total7InchPulls, total10InchPulls, totalCost, totalSqft };
  };

  const clearAll = () => {
    if (window.confirm('Clear all cabinet quantities?')) {
      setCabinetQuantities({});
      localStorage.removeItem('cabinetQuantities');
    }
  };

  const totals = calculateTotals();

  const filteredCategories = cabinetSearchTerm 
    ? Object.entries(CABINET_CATEGORIES).reduce((acc, [category, cabinets]) => {
        const filtered = cabinets.filter(c => 
          c.model.toLowerCase().includes(cabinetSearchTerm .toLowerCase())
        );
        if (filtered.length > 0) {
          acc[category] = filtered;
        }
        return acc;
      }, {})
    : CABINET_CATEGORIES;

  // Calculate Add-On totals
  const addOnTotals = useMemo(() => {
    return addOns.reduce((acc, addon) => {
      const price = parseFloat(addon.price) || 0;
      const overages = parseFloat(addon.overages) || 0;
      
      // Only apply markup to price, not overages
      const priceWith25 = overages > 0 ? 0 : (price * (1 + profitMarginPercent / 100));
      const rvcgProfit = overages > 0 ? 0 : (price * profitMarginPercent / 100);
      
      const amountPaid = parseFloat(addon.amountPaid) || 0;
      const stillDue = overages > 0 ? overages - amountPaid : priceWith25 - amountPaid;
      
      return {
        price: acc.price + price,
        overages: acc.overages + overages,
        priceWith25: acc.priceWith25 + priceWith25,
        rvcgProfit: acc.rvcgProfit + rvcgProfit,
        amountPaid: acc.amountPaid + amountPaid,
        stillDue: acc.stillDue + stillDue
      };
    }, { price: 0, overages: 0, priceWith25: 0, rvcgProfit: 0, amountPaid: 0, stillDue: 0 });
  }, [addOns, profitMarginPercent]);

  const getItemKey = (category, item) => `${category}|||${item}`;

  const getItemData = (category, item) => {
    const key = getItemKey(category, item);
    const defaultData = {
      originalBidCost: 0,
      contractorName: '',
      jobComplete: false,
      draws: [0, 0, 0, 0, 0, 0, 0, 0],
      changeOrders: 0
    };
    
    // Ensure swornData and items exist
    if (!swornData || !swornData.items) {
      return defaultData;
    }
    
    return swornData.items[key] || defaultData;
  };

  const calculateBalance = (itemData, category = null, item = null) => {
    if (category === 'Lumber Package' && item === 'Big C Lumber Package Total') {
      const subItems = [
        'Framing Package',
        'Basement Framing',
        'Trusses',
        'Roofing Materials',
        'Windows -Patio Door(s)',
        'Exterior Service Door(s)',
        'Big C Install Labor',
        'Siding Material',
        'Drywall',
        'Trim & Interior Doors Mat Cost',
        'Deck'
      ];
      
      return subItems.reduce((sum, subItem) => {
        const subItemData = getItemData('Lumber Package', subItem);
        const subBalance = (parseFloat(subItemData.originalBidCost) || 0) + 
                          (parseFloat(subItemData.changeOrders) || 0) - 
                          subItemData.draws.reduce((total, draw) => total + (parseFloat(draw) || 0), 0);
        return sum + subBalance;
      }, 0);
    }
    
    const total = itemData.draws.reduce((sum, draw) => sum + (parseFloat(draw) || 0), 0);
    let balance = (parseFloat(itemData.originalBidCost) || 0) + (parseFloat(itemData.changeOrders) || 0) - total;
    return balance;
  };

  const saveItemData = async (category, item, field, value) => {
    if (!currentProject) return;
    
    const key = getItemKey(category, item);
    const currentData = getItemData(category, item);
    
    let updatedItemData = { ...currentData };
    
    if (field === 'jobComplete') {
      updatedItemData.jobComplete = value;
    } else if (field.startsWith('draw_')) {
      const drawIndex = parseInt(field.split('_')[1]);
      updatedItemData.draws[drawIndex] = parseFloat(value) || 0;
    } else if (field.startsWith('date_')) {
      const dateIndex = parseInt(field.split('_')[1]);
      updatedItemData.dates[dateIndex] = parseFloat(value) || 0;
    } else {
      updatedItemData[field] = field === 'originalBidCost' || field === 'changeOrders' 
        ? parseFloat(value) || 0 
        : value;
    }

    const updatedSwornData = {
      ...swornData,
      items: {
        ...swornData.items,
        [key]: updatedItemData
      }
    };

    // Auto-calculate Plumbing Rough and Finish when Plumbing Estimate changes
    if (category === 'Plumbing' && item === 'Plumbing Estimate' && field === 'originalBidCost') {
      const estimateValue = parseFloat(value) || 0;
      const roughValue = estimateValue * 0.6;
      const finishValue = estimateValue - roughValue;
      
      // Update Plumbing Rough
      const roughKey = getItemKey('Plumbing', 'Plumbing Rough');
      const roughData = getItemData('Plumbing', 'Plumbing Rough');
      updatedSwornData.items[roughKey] = {
        ...roughData,
        originalBidCost: roughValue
      };
      
      // Update Plumbing Finish
      const finishKey = getItemKey('Plumbing', 'Plumbing Finish');
      const finishData = getItemData('Plumbing', 'Plumbing Finish');
      updatedSwornData.items[finishKey] = {
        ...finishData,
        originalBidCost: finishValue
      };
    }

    // Auto-calculate Electrical Rough and Finish when Electrical Estimate changes
    if (category === 'Electrical' && item === 'Electrical Estimate' && field === 'originalBidCost') {
      const estimateValue = parseFloat(value) || 0;
      const roughValue = estimateValue * 0.75;
      const finishValue = estimateValue - roughValue;
      
      // Update Electrical Rough
      const roughKey = getItemKey('Electrical', 'Electric Rough In');
      const roughData = getItemData('Electrical', 'Electric Rough In');
      updatedSwornData.items[roughKey] = {
        ...roughData,
        originalBidCost: roughValue
      };
      
      // Update Electrical Finish
      const finishKey = getItemKey('Electrical', 'Electric Finish');
      const finishData = getItemData('Electrical', 'Electric Finish');
      updatedSwornData.items[finishKey] = {
        ...finishData,
        originalBidCost: finishValue
      };
    }

    // Auto-calculate HVAC Rough and Finish when HVAC Estimate changes
    if (category === 'HVAC' && item === 'HVAC Estimate' && field === 'originalBidCost') {
      const estimateValue = parseFloat(value) || 0;
      const roughValue = estimateValue * 0.8;
      const finishValue = estimateValue - roughValue;
      
      // Update Electrical Rough
      const roughKey = getItemKey('HVAC', 'HVAC Rough');
      const roughData = getItemData('HVAC', 'HVAC Rough');
      updatedSwornData.items[roughKey] = {
        ...roughData,
        originalBidCost: roughValue
      };
      
      // Update Electrical Finish
      const finishKey = getItemKey('HVAC', 'HVAC Final');
      const finishData = getItemData('HVAC', 'HVAC Final');
      updatedSwornData.items[finishKey] = {
        ...finishData,
        originalBidCost: finishValue
      };
    }

    // Auto-calculate trim labor 
    if (squareFeet !== '' || squareFeet !== 0) {
      const estimateValue = parseFloat(squareFeet) || 0;
      const roughValue = estimateValue * 3.55;
      
      // Update Electrical Rough
      const roughKey = getItemKey('Millwork', 'Trim Labor');
      const roughData = getItemData('Millwork', 'Trim Labor');
      updatedSwornData.items[roughKey] = {
        ...roughData,
        originalBidCost: roughValue
      };
    }

    setSwornData(updatedSwornData);

    // Save to Firebase
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        items: updatedSwornData.items,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving item data:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  /* const EXCLUDED_ESTIMATE_ROWS = new Set([ 
    // Category estimate rows
    'Lumber Package|||Big C Lumber Package Total',
    'Concrete|||Concrete Estimate',
    'Gutters|||Gutters Estimate',
    'Outdoor|||Decks - Patio Estimate',
    'Appliances|||Appliances Estimate',
    'Plumbing|||Plumbing Estimate',
    'Framing|||Quote',
    'Electrical|||Electrical Estimate',
    'HVAC|||HVAC Estimate',
    'Insulation|||Insulation Estimate',
    'Drywall|||Drywall Estimate',
    'Paint|||Paint Estimate',
    'Flooring|||Flooring Estimate',
    'Fireplace|||Fireplace Estimate',
    'Cabinetry|||Cabinetry Estimate',
    'Millwork|||Millwork Estimate'
  ]); */

  const EXCLUDED_ESTIMATE_ROWS = new Set([]);

  const EXCLUDED_TOTAL_ROWS = new Set([
    // Section Total rows (these are the missing exclusions!)
    'Site|||Site Total',
    'Excavation|||Excavation Total',
    'Foundation|||Foundation Total',
    'Waterproofing|||Waterproofing Total',
    'Lumber Package|||Lumber Package Total',
    'Concrete|||Concrete Total',
    'Framing|||Framing Total',
    'Garage|||Garage Total',
    'Siding|||Siding Total',
    'Gutters|||Gutters Total',
    'Outdoor|||Outdoor Total',
    'Appliances|||Appliances Total',
    'Plumbing|||Plumbing Total',
    'Electrical|||Electrical Total',
    'HVAC|||HVAC Total',
    'Insulation|||Insulation Total',
    'Drywall|||Drywall Total',
    'Paint|||Paint Total',
    'Flooring|||Flooring Total',
    'Fireplace|||Fireplace Total',
    'Cabinetry|||Cabinetry Total',
    'Millwork|||Millwork Total',
    'Utilities|||Utilities Total',
    'Misc Expenses|||Misc Total',
    
    // Category estimate rows
    'Lumber Package|||Big C Lumber Package Total',
    'Concrete|||Concrete Estimate',
    'Gutters|||Gutters Estimate',
    'Outdoor|||Decks - Patio Estimate',
    'Appliances|||Appliances Estimate',
    'Plumbing|||Plumbing Estimate',
    'Framing|||Quote',
    'Electrical|||Electrical Estimate',
    'HVAC|||HVAC Estimate',
    'Insulation|||Insulation Estimate',
    'Drywall|||Drywall Estimate',
    'Paint|||Paint Estimate',
    'Flooring|||Flooring Estimate',
    'Fireplace|||Fireplace Estimate',
    'Cabinetry|||Cabinetry Estimate',
    'Millwork|||Millwork Estimate'
  ]);

  // Calculate total overages (negative balances)
  const totalOverages = Object.entries(CATEGORIES).reduce((total, [category, data]) => {
    let items = category === 'Misc Expenses' ? miscExpenses.map(exp => exp.name) : data.items;
    
    // Add custom items
    if (customItems[category]) {
      items = [...items, ...customItems[category]];
    }
    
    return total + items.reduce((sum, item) => {
      const key = getItemKey(category, item);
      
      // Use EXCLUDED_ESTIMATE_ROWS instead of manual IF statements
      if (EXCLUDED_ESTIMATE_ROWS.has(key)) {
        return sum;
      }

      const itemData = getItemData(category, item);
      const balance = calculateBalance(itemData, category, item);
      
      if (balance < 0) {
        return sum + balance;
      }
      return sum;
    }, 0);
  }, 0);

  // Calculate total under budget (positive balances)
  const totalUnderBudget = Object.entries(CATEGORIES).reduce((total, [category, data]) => {
    let items = category === 'Misc Expenses' ? miscExpenses.map(exp => exp.name) : data.items;
    
    // Add custom items
    if (customItems[category]) {
      items = [...items, ...customItems[category]];
    }
    
    return total + items.reduce((sum, item) => {
      const key = getItemKey(category, item);
      
      // Use EXCLUDED_ESTIMATE_ROWS
      if (EXCLUDED_ESTIMATE_ROWS.has(key)) {
        return sum;
      }

      const itemData = getItemData(category, item);
      const balance = calculateBalance(itemData, category, item);
      
      if (balance > 0) {
        return sum + balance;
      }
      return sum;
    }, 0);
  }, 0);

  const totalTowardsCompletion = totalUnderBudget - Math.abs(totalOverages);

  // This finds jobs where Balance = Original Bid (no draws pulled yet - job not started)
  const unpaidJobsCost = Object.entries(CATEGORIES).reduce((total, [category, data]) => {
    let items = category === 'Misc Expenses' ? miscExpenses.map(exp => exp.name) : data.items;
    const allUnpaidItems = [];
    
    if (customItems[category]) {
      items = [...items, ...customItems[category]];
    }
    
    return total + items.reduce((sum, item) => {
      const key = getItemKey(category, item);
      
      if (EXCLUDED_ESTIMATE_ROWS.has(key)) {
        console.log('❌ EXCLUDED:', key);
        return sum;
      }
      
      const itemData = getItemData(category, item);
      const originalBid = parseFloat(itemData.originalBidCost) || 0;
      const balance = calculateBalance(itemData, category, item);
      
      // Match Google Sheets logic: N column equals B column
      if (originalBid !== 0 && Math.abs(balance - originalBid) < 0.01) {
        console.log('✓ UNPAID:', key, 'Bid:', formatCurrency(originalBid), 'Balance:', formatCurrency(balance));
        return sum + originalBid;
      } else if (originalBid !== 0) {
        console.log('⊗ PAID/PARTIAL:', key, 'Bid:', formatCurrency(originalBid), 'Balance:', formatCurrency(balance));
      }

      if (originalBid !== 0 && Math.abs(balance - originalBid) < 0.01) {
        allUnpaidItems.push({key, originalBid, balance});
        console.log('✓ UNPAID:', key, 'Bid:', formatCurrency(originalBid));
        return sum + originalBid;
      }

      console.log('Total unpaid items:', allUnpaidItems.length);
      console.log('All unpaid items:', allUnpaidItems);

      return sum;
    }, 0);
  }, 0);

  console.log('TOTAL Unpaid Jobs Cost:', formatCurrency(unpaidJobsCost));

  const projectedOverages = totalTowardsCompletion - unpaidJobsCost;

  const addMiscExpense = async () => {
    if (!currentProject || !newMiscExpenseName.trim()) return;
    
    const newExpense = {
      id: Date.now(),
      name: newMiscExpenseName.trim()
    };
    
    const updatedExpenses = [...miscExpenses, newExpense];
    setMiscExpenses(updatedExpenses);
    setNewMiscExpenseName('');
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        miscExpenses: updatedExpenses,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error adding misc expense:', error);
    }
  };

  const deleteMiscExpense = async (id) => {
    if (!currentProject) return;
    
    const updatedExpenses = miscExpenses.filter(exp => exp.id !== id);
    setMiscExpenses(updatedExpenses);
    
    // Also remove the item data
    const expenseName = miscExpenses.find(exp => exp.id === id)?.name;
    if (expenseName) {
      const key = getItemKey('Misc Expenses', expenseName);
      const updatedItems = { ...swornData.items };
      delete updatedItems[key];
      
      try {
        const docRef = doc(db, 'swornStatements', currentProject.id);
        await setDoc(docRef, {
          miscExpenses: updatedExpenses,
          items: updatedItems,
          updatedAt: Timestamp.now()
        }, { merge: true });
        
        setSwornData({
          ...swornData,
          items: updatedItems
        });
      } catch (error) {
        console.error('Error deleting misc expense:', error);
      }
    }
  };

  const addHistoricalJob = () => {
    const newJob = {
      id: Date.now(),
      customerName: '',
      foundation: 0,
      framing: 0,
      bigC: 0,
      sqft: 0
    };
    
    setHistoricalJobs([...historicalJobs, newJob]);
  };

  const updateHistoricalJob = (id, field, value) => {
    setHistoricalJobs(historicalJobs.map(job => 
      job.id === id ? { ...job, [field]: value } : job
    ));
  };

  const saveHistoricalJobs = async () => {
    try {
      const docRef = doc(db, 'swornStatements', 'historicalJobs');
      await setDoc(docRef, {
        jobs: historicalJobs,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving historical jobs:', error);
    }
  };

  const deleteHistoricalJob = async (id) => {
    const updatedJobs = historicalJobs.filter(job => job.id !== id);
    
    setHistoricalJobs(updatedJobs);
    
    try {
      const docRef = doc(db, 'swornStatements', 'historicalJobs');
      await setDoc(docRef, {
        jobs: updatedJobs,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error deleting historical job:', error);
    }
  };

  const calculatePricePerSqFt = () => {
    if (historicalJobs.length === 0) return { foundation: 0, framing: 0, bigC: 0, totals: { foundation: 0, framing: 0, bigC: 0, sqft: 0 } };
    
    const totals = historicalJobs.reduce((acc, job) => ({
      foundation: acc.foundation + (parseFloat(job.foundation) || 0),
      framing: acc.framing + (parseFloat(job.framing) || 0),
      bigC: acc.bigC + (parseFloat(job.bigC) || 0),
      sqft: acc.sqft + (parseFloat(job.sqft) || 0)
    }), { foundation: 0, framing: 0, bigC: 0, sqft: 0 });

    return {
      foundation: totals.sqft > 0 ? totals.foundation / totals.sqft : 0,
      framing: totals.sqft > 0 ? totals.framing / totals.sqft : 0,
      bigC: totals.sqft > 0 ? totals.bigC / totals.sqft : 0,
      totals
    };
  };

  const applyPricingToFoundation = async () => {
    if (squareFeet === 0) {
      alert('Please enter square feet for this project first.');
      return;
    }
    
    const pricing = calculatePricePerSqFt();
    const estimatedFoundationCost = squareFeet * pricing.foundation;
    const estimatedLumberCost = squareFeet * pricing.framing;
    const estimatedBigCCost = squareFeet * pricing.bigC;
    
    try {
      await saveItemData('Footings and Foundation', 'Footings and Foundation', 'originalBidCost', estimatedFoundationCost);
      await saveItemData('Framing', 'Quote', 'originalBidCost', estimatedLumberCost);
      await saveItemData('Lumber Package', 'Big C Lumber Package Total', 'originalBidCost', estimatedBigCCost);
      
      setShowPricingModal(false);
    } catch (error) {
      console.error('Error applying pricing:', error);
    }
  };

  const saveProjectInfo = async () => {
    if (!currentProject) return;
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        contractPrice: parseFloat(contractPrice) || 0,
        downPayment: parseFloat(downPayment) || 0,
        squareFeet: parseFloat(squareFeet) || 0,
        cabinetPulls: parseFloat(cabinetPulls) || 0,
        drawerPulls: parseFloat(drawerPulls) || 0,
        profitMarginPercent: parseFloat(profitMarginPercent) || 25,
        drawsReceived: drawsReceived,
        datesReceived: datesReceived,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving project info:', error);
    }
  };

  const updateDrawCheckbox = async (drawIndex, checked) => {
    const updatedCheckboxes = [...drawCheckboxes];
    updatedCheckboxes[drawIndex] = checked;
    setDrawCheckboxes(updatedCheckboxes);
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        drawCheckboxes: updatedCheckboxes,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating draw checkbox:', error);
    }
  };

  const updateDrawReceived = async (drawIndex, value) => {
    const updatedDraws = [...drawsReceived];
    updatedDraws[drawIndex] = parseFloat(value) || 0;
    setDrawsReceived(updatedDraws);
    
    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        drawsReceived: updatedDraws,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating draw received:', error);
    }
  }

  const updateDatesReceived = async (dateIndex, value) => {
    const updatedDates = [...datesReceived];
    updatedDates[dateIndex] = value;

    setDatesReceived(updatedDates);

    try {
      const docRef = doc(db, 'swornStatements', currentProject.id);
      await setDoc(docRef, {
        datesReceived: updatedDates,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating date received:', error);
    }
  };

  const handleCellClick = (category, item, field, drawIndex = null) => {
    const itemData = getItemData(category, item);
    let value = '';
    
    if (field === 'jobComplete') {
      saveItemData(category, item, 'jobComplete', !itemData.jobComplete);
      return;
    } else if (drawIndex !== null) {
      value = itemData.draws[drawIndex] || 0;
      setEditingCell(`${category}|||${item}|||draw_${drawIndex}`);
    } else {
      value = itemData[field] || '';
      setEditingCell(`${category}|||${item}|||${field}`);
    }
    
    setEditValue(value);
  };

  const handleCellSave = async (category, item, field, drawIndex = null) => {
    const fieldKey = drawIndex !== null ? `draw_${drawIndex}` : field;
    await saveItemData(category, item, fieldKey, editValue);
    setEditingCell(null);
    setEditValue('');
  };

  const calculateCategoryTotal = (category, field, drawIndex = null) => {
    let items = CATEGORIES[category].items;
  
    // For Misc Expenses, use the dynamic list
    if (category === 'Misc Expenses') {
      items = miscExpenses.map(exp => exp.name);
    }
    
    // Add custom items for this category
    if (customItems[category]) {
      items = [...items, ...customItems[category]];
    }
    
    return items.reduce((sum, item) => {
      // Skip "Big C Lumber Package Total" in Lumber Package category for originalBidCost and balance calculations
      if (category === 'Lumber Package' && item === 'Big C Lumber Package Total' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Concrete" in Concrete category for originalBidCost and balance calculations
      if (category === 'Concrete' && item === 'Concrete Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Gutters" in Framing category for originalBidCost and balance calculations
      if (category === 'Gutters' && item === 'Gutters Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Decks" in Outdoor category for originalBidCost and balance calculations
      if (category === 'Outdoor' && item === 'Decks - Patio Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Appliances Estimate" in Appliances category for originalBidCost and balance calculations
      if (category === 'Appliances' && item === 'Appliances Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Plumbing Estimate" in Framing category for originalBidCost and balance calculations
      if (category === 'Plumbing' && item === 'Plumbing Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Quote" in Framing category for originalBidCost and balance calculations
      if (category === 'Framing' && item === 'Quote' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Electrical" in Electrical category for originalBidCost and balance calculations
      if (category === 'Electrical' && item === 'Electrical Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "HVAC" in HVAC category for originalBidCost and balance calculations
      if (category === 'HVAC' && item === 'HVAC Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Insulation" in Insulation category for originalBidCost and balance calculations
      if (category === 'Insulation' && item === 'Insulation Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Drywall" in Drywall category for originalBidCost and balance calculations
      if (category === 'Drywall' && item === 'Drywall Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Paint Estimate" in Paint category for originalBidCost and balance calculations
      if (category === 'Paint' && item === 'Paint Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Flooring Estimate" in Flooring category for originalBidCost and balance calculations
      if (category === 'Flooring' && item === 'Flooring Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }
      
      // Skip "Fireplace Estimate" in Fireplace category for originalBidCost and balance calculations
      if (category === 'Fireplace' && item === 'Fireplace Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Cabinetry Estimate" in Cabinetry category for originalBidCost and balance calculations
      if (category === 'Cabinetry' && item === 'Cabinetry Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Millwork Estimate" in Millwork category for originalBidCost and balance calculations
      if (category === 'Millwork' && item === 'Millwork Estimate' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      }

      // Skip "Cleanings - Final Clean" in Final category for originalBidCost and balance calculations
      /* if (category === 'Final' && item === 'Cleanings - Final Clean' && (field === 'balance' || field === 'originalBidCost')) {
        return sum;
      } */

      const itemData = getItemData(category, item);
      if (field === 'originalBidCost') {
        return sum + (parseFloat(itemData.originalBidCost) || 0);
      } else if (field === 'changeOrders') {
        return sum + (parseFloat(itemData.changeOrders) || 0);
      } else if (field === 'balance') {
        return sum + calculateBalance(itemData, category, item);;
      } else if (drawIndex !== null) {
        return sum + (parseFloat(itemData.draws[drawIndex]) || 0);
      }
      return sum;
    }, 0);
  };

  const calculateGrandTotal = (field, drawIndex = null) => {
    return Object.keys(CATEGORIES).reduce((sum, category) => {
      return sum + calculateCategoryTotal(category, field, drawIndex);
    }, 0);
  };

  const debugOveragesCalculation = () => {
    console.log('=== OVERAGES CALCULATION DEBUG ===');
    
    let totalOverages = 0;
    let totalUnderBudget = 0;
    
    Object.entries(CATEGORIES).forEach(([category, data]) => {
      let items = category === 'Misc Expenses' ? miscExpenses.map(exp => exp.name) : data.items;
      
      // Add custom items
      if (customItems[category]) {
        items = [...items, ...customItems[category]];
      }
      
      let categoryOverages = 0;
      let categoryUnderBudget = 0;
      
      items.forEach((item) => {
        // Skip estimate/total rows
        const key = getItemKey(category, item);
        if (EXCLUDED_ESTIMATE_ROWS.has(key)) {
          console.log(`SKIPPING: ${key}`);
          return;
        }
        
        const itemData = getItemData(category, item);
        const balance = calculateBalance(itemData, category, item);
        
        if (balance < 0) {
          categoryOverages += balance;
          console.log(`OVERAGE - ${category}|||${item}: ${formatCurrency(balance)}`);
        } else if (balance > 0) {
          categoryUnderBudget += balance;
          console.log(`UNDER - ${category}|||${item}: ${formatCurrency(balance)}`);
        }
      });
      
      if (categoryOverages !== 0 || categoryUnderBudget !== 0) {
        console.log(`${category} TOTAL - Overages: ${formatCurrency(categoryOverages)}, Under: ${formatCurrency(categoryUnderBudget)}`);
      }
      
      totalOverages += categoryOverages;
      totalUnderBudget += categoryUnderBudget;
    });
    
    console.log('=== FINAL TOTALS ===');
    console.log(`Total Overages: ${formatCurrency(totalOverages)}`);
    console.log(`Total Under Budget: ${formatCurrency(totalUnderBudget)}`);
    console.log(`Balance Towards Completion: ${formatCurrency(totalUnderBudget + totalOverages)}`);
    console.log('======================');
  };

  // Calculate contractor performance metrics
  const getContractorMetrics = () => {
    const contractorData = {};
    
    Object.entries(CATEGORIES).forEach(([category, data]) => {
      let items = data.items;
      if (category === 'Misc Expenses') {
        items = miscExpenses.map(exp => exp.name);
      }
      
      items.forEach(item => {
        const itemData = getItemData(category, item);
        const contractorName = itemData.contractorName;
        
        if (contractorName) {
          if (!contractorData[contractorName]) {
            contractorData[contractorName] = {
              totalBid: 0,
              totalPaid: 0,
              changeOrders: 0,
              jobsComplete: 0,
              totalJobs: 0,
              items: []
            };
          }
          
          const totalPaid = itemData.draws.reduce((sum, draw) => sum + (parseFloat(draw) || 0), 0);
          contractorData[contractorName].totalBid += parseFloat(itemData.originalBidCost) || 0;
          contractorData[contractorName].totalPaid += totalPaid;
          contractorData[contractorName].changeOrders += parseFloat(itemData.changeOrders) || 0;
          contractorData[contractorName].totalJobs += 1;
          if (itemData.jobComplete) contractorData[contractorName].jobsComplete += 1;
          contractorData[contractorName].items.push({ category, item, ...itemData });
        }
      });
    });
    
    return Object.entries(contractorData).map(([name, data]) => ({
      name,
      ...data,
      overagePercent: data.totalBid > 0 ? ((data.changeOrders / data.totalBid) * 100) : 0,
      completionRate: data.totalJobs > 0 ? ((data.jobsComplete / data.totalJobs) * 100) : 0,
      remaining: data.totalBid - data.totalPaid
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    let csv = 'Category,Item,Original Bid Cost,Contractor,Complete,Draw 1,Draw 2,Draw 3,Draw 4,Draw 5,Draw 6,Draw 7,Draw 8,Change Orders,Balance\n';
    
    Object.entries(CATEGORIES).forEach(([category, data]) => {
      let items = data.items;
      if (category === 'Misc Expenses') {
        items = miscExpenses.map(exp => exp.name);
      }
      
      items.forEach(item => {
        const itemData = getItemData(category, item);
        const balance = calculateBalance(itemData, category, item);
        const row = [
          category,
          item,
          itemData.originalBidCost,
          itemData.contractorName || '',
          itemData.jobComplete ? 'Yes' : 'No',
          ...itemData.draws,
          itemData.changeOrders,
          balance
        ];
        csv += row.map(cell => `"${cell}"`).join(',') + '\n';
      });
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sworn-statement-${currentProject?.name || 'export'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };


  const totalBidCosts = calculateGrandTotal('originalBidCost');
  const totalChangeOrders = calculateGrandTotal('changeOrders');
  const totalDrawsPulled = Array(8).fill(0).reduce((sum, _, idx) => sum + calculateGrandTotal('draw', idx), 0);
  const runningContractPrice = contractPrice + addOnTotals.priceWith25 + addOnTotals.rvcgProfit;
  const totalPotentialProfit = runningContractPrice - totalBidCosts + addOnTotals.rvcgProfit;
  const costPerSqFt = squareFeet > 0 ? runningContractPrice / squareFeet : 0;
  
  // Calculate draw totals for summary section
  const drawTotals = Array(8).fill(0).map((_, idx) => calculateGrandTotal('draw', idx));
  const drawPullTotal = calculateGrandTotal('draw', selectedDraw);
  const drawPullReceiveTotal = calculateGrandTotal('draw', selectedDraw);
  const drawReceivedTotal = drawTotals.reduce((sum, amount) => sum + amount, 0);
  const percentFundsPulled = runningContractPrice > 0 ? ((drawReceivedTotal / runningContractPrice) * 100).toFixed(2) : 0;
  const fundsLeftToPull = runningContractPrice - drawsReceived.reduce((sum, val) => sum + (parseFloat(val) || 0), 0) - downPayment;
  const surplus = drawsReceived[selectedDraw] - drawPullTotal;
  const percentagePulled = ((drawsReceived.reduce((sum, val) => sum + (parseFloat(val) || 0), 0)+ downPayment) / runningContractPrice * 100).toFixed(2);
  const overagesPercent = (totalOverages / runningContractPrice) * 100;
  const underagesPercent = (totalUnderBudget / runningContractPrice) * 100;
  const profitPercent = (totalPotentialProfit / runningContractPrice) * 100;

  //Add-ons
  const [newAddOnName, setNewAddOnName] = useState('');
  const contractorMetrics = getContractorMetrics();

  if (loading) {
    return <LoadingSpinner message="Loading sworn statement..." />;
  }

  // Update a concrete option in Firebase
  const updateConcreteOption = async (id, field, value) => {
    try {
      const optionRef = doc(db, 'concreteOptions', id);
      await updateDoc(optionRef, { [field]: value });
      
      // Update local state
      setConcreteOptions(prev => 
        prev.map(opt => opt.id === id ? { ...opt, [field]: value } : opt)
      );
    } catch (error) {
      console.error('Error updating concrete option:', error);
    }
  };

  // Update a concrete option in Firebase
  const updateConcreteSQFTOption = async (id, field, value) => {
    try {
      const optionRef = doc(db, 'swornStatements', id);
      await updateDoc(optionRef, { [field]: value });
      
      // Update local state
      setConcreteOptions(prev => 
        prev.map(opt => opt.id === id ? { ...opt, [field]: value } : opt)
      );
    } catch (error) {
      console.error('Error updating concrete option:', error);
    }
  };

  const updateConcreteItemCost = async (optionName, totalCost) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('User not authenticated');
        throw new Error('User not authenticated');
      }

      if (!currentProject?.id) {
        console.error('No project ID available');
        throw new Error('No project ID available');
      }
      
      const statementRef = doc(db, 'swornStatements', currentProject.id);
      const statementDoc = await getDoc(statementRef);
      
      if (!statementDoc.exists()) {
        console.error('Sworn statement not found');
        throw new Error('Sworn statement not found');
      }
      
      const data = statementDoc.data();
      const items = data.items || {};
      
      // Map option names to exact Firebase item names
      const itemMapping = {
        'driveway': 'Driveway',
        'garage floor': 'Garage Floor',
        'garage': 'Garage Floor',
        'basement floor': 'Basement',
        'basement': 'Basement',
        'porch 1-2 overhang': 'Front Porch',
        'porch 1-2" overhang': 'Front Porch',
        'porch 1-2\' overhang': 'Front Porch',
        'front porch': 'Front Porch',
        'back porch': 'Back Porch',
        'sidewalk': 'Sidewalks',
        'patio': 'Patio',
        '4x4 service door': 'Service Door Pad',
        '4x4x service door': 'Service Door Pad',
        'service door': 'Service Door Pad',
        '3ft steps': 'Steps',
        'steps': 'Steps',
        'slab': 'Slab',
        'tile under sidewalk': 'Misc Concrete Expenses',
        'east bob cat time': 'Misc Concrete Expenses',
        'visqueen': 'Misc Concrete Expenses',
        'extras': 'Misc Concrete Expenses',
        'extras - winter charge, nc, calcium': 'Misc Concrete Expenses',
        'extras - winter charge, nc, calcium (per yard not ft)': 'Misc Concrete Expenses',
        'misc concrete expenses': 'Misc Concrete Expenses' // Add direct mapping
      };
      
      // Normalize the option name
      const normalizedOption = optionName.toLowerCase()
        .replace(/"/g, '')
        .replace(/'/g, '')
        .replace(/ floor$/i, '')
        .trim();
      
      // Get the mapped item name
      const mappedItemName = itemMapping[normalizedOption];
      
      if (!mappedItemName) {
        console.warn(`No mapping found for option: "${optionName}"`);
        return;
      }
      
      // Find EXACT matching key in existing items
      const matchingKey = Object.keys(items).find(key => {
        const parts = key.split('|||');
        if (parts.length !== 2 || parts[0].toLowerCase() !== 'concrete') {
          return false;
        }
        
        const itemName = parts[1].trim();
        return itemName === mappedItemName;
      });
      
      // If totalCost is 0 or empty, delete the item
      if (!totalCost || totalCost === 0) {
        if (matchingKey) {
          const updateObj = {};
          updateObj[`items.${matchingKey}`] = deleteField();
          
          await updateDoc(statementRef, updateObj);
          
          // Update local state
          setExistingConcreteItems(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
              if (updated[key].key === matchingKey) {
                delete updated[key];
              }
            });
            return updated;
          });
        }
        return;
      }
      
      // Round to 2 decimal places
      const roundedCost = parseFloat(totalCost.toFixed(2));
      
      if (matchingKey) {
        // Update existing item
        await updateDoc(statementRef, {
          [`items.${matchingKey}.originalBidCost`]: roundedCost
        });
        
        // Update local state
        const itemName = matchingKey.split('|||')[1];
        setExistingConcreteItems(prev => ({
          ...prev,
          [itemName]: {
            originalBidCost: roundedCost,
            key: matchingKey
          }
        }));
      } else {
        // Create new item
        const newKey = `Concrete|||${mappedItemName}`;
        
        const newItem = {
          changeOrders: 0,
          contractorName: "",
          draws: [0, 0, 0, 0, 0, 0, 0, 0],
          jobComplete: false,
          originalBidCost: roundedCost
        };
        
        await updateDoc(statementRef, {
          [`items.${newKey}`]: newItem
        });
        
        // Update local state
        setExistingConcreteItems(prev => ({
          ...prev,
          [mappedItemName]: {
            originalBidCost: roundedCost,
            key: newKey
          }
        }));
      }
      
    } catch (error) {
      console.error('Error updating concrete item cost:', error);
      throw error;
    }
  };

  return (
    <>
      <PageHeader
        currentPage="swornstatements"
        onNavigate={onNavigate}
        currentProject={currentProject}
        projects={projects}
        onProjectChange={onProjectChange}
        userRole={userRole}
      />
       <div style={{ minHeight: '100vh'}}>
        {/* Main Content */}
        {!currentProject ? (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '3rem', 
            margin: '1.5rem',
            borderRadius: '8px', 
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ color: '#666', marginBottom: '0.5rem' }}>No Project Selected</h2>
            <p style={{ color: '#999' }}>Please select a project to view sworn statement</p>
          </div>
        ) : (
          <>
            {/* Historical Pricing Modal */}
            {showPricingModal && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>Historical Job Pricing</h2>
                    <button
                      onClick={loadAllProjectsHistoricalData}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                      </svg>
                      Refresh from Projects
                    </button>
                  </div>
                  <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '14px' }}>
                    Automatically loaded from all existing projects. Add manual entries or edit as needed.
                  </p>
                  
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Customer Name</th>
                          <th style={{ padding: '12px', textAlign: 'right' }}>Foundation</th>
                          <th style={{ padding: '12px', textAlign: 'right' }}>Framing</th>
                          <th style={{ padding: '12px', textAlign: 'right' }}>Big C</th>
                          <th style={{ padding: '12px', textAlign: 'right' }}>SQFT</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Type</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historicalJobs.map((job) => (
                          <tr key={job.id} style={{ 
                            borderBottom: '1px solid #e9ecef',
                            backgroundColor: job.projectId ? '#f0f9ff' : 'white' 
                          }}>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="text"
                                value={job.customerName}
                                onChange={(e) => updateHistoricalJob(job.id, 'customerName', e.target.value)}
                                onBlur={saveHistoricalJobs}
                                placeholder="Customer name"
                                readOnly={job.projectId} // Make project names read-only
                                style={{ 
                                  width: '100%', 
                                  padding: '4px 8px', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '4px', 
                                  fontSize: '12px',
                                  backgroundColor: job.projectId ? '#f0f9ff' : 'white',
                                  cursor: job.projectId ? 'not-allowed' : 'text'
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="number"
                                step="0.01"
                                value={job.foundation}
                                onChange={(e) => updateHistoricalJob(job.id, 'foundation', e.target.value)}
                                onBlur={(e) => {
                                  // Round to 2 decimals when user leaves the field
                                  const rounded = parseFloat(e.target.value).toFixed(2);
                                  updateHistoricalJob(job.id, 'foundation', rounded);
                                  saveHistoricalJobs();
                                }}
                                placeholder="0.00"
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="number"
                                step="0.01"
                                value={job.framing}
                                onChange={(e) => updateHistoricalJob(job.id, 'framing', e.target.value)}
                                onBlur={saveHistoricalJobs}
                                placeholder="0.00"
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="number"
                                step="0.01"
                                value={job.bigC}
                                onChange={(e) => updateHistoricalJob(job.id, 'bigC', e.target.value)}
                                onBlur={saveHistoricalJobs}
                                placeholder="0.00"
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="number"
                                value={job.sqft}
                                onChange={(e) => updateHistoricalJob(job.id, 'sqft', e.target.value)}
                                onBlur={saveHistoricalJobs}
                                placeholder="0"
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <span style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                backgroundColor: job.projectId ? '#e3f2fd' : '#fff3e0',
                                color: job.projectId ? '#1976d2' : '#f57c00',
                                fontWeight: '500'
                              }}>
                                {job.projectId ? 'Auto' : 'Manual'}
                              </span>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => deleteHistoricalJob(job.id)}
                                style={{ padding: '4px 8px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                <Trash2 style={{ width: '12px', height: '12px' }} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {historicalJobs.length > 0 && (() => {
                          const pricing = calculatePricePerSqFt();
                          return (
                            <>
                              <tr style={{ backgroundColor: '#f8f9fa', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
                                <td style={{ padding: '12px' }}>Total</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(pricing.totals.foundation.toFixed(2))}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(pricing.totals.framing.toFixed(2))}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(pricing.totals.bigC.toFixed(2))}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{pricing.totals.sqft.toLocaleString()}</td>
                                <td></td>
                              </tr>
                              <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700', fontSize: '13px' }}>
                                <td style={{ padding: '12px', color: '#1976D2' }}>Price Per SQFT</td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#1976D2' }}>{formatCurrency(pricing.foundation.toFixed(2))}</td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#1976D2' }}>{formatCurrency(pricing.framing.toFixed(2))}</td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#1976D2' }}>{formatCurrency(pricing.bigC)}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}></td>
                                <td></td>
                              </tr>
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>

                  <button
                    onClick={addHistoricalJob}
                    style={{ padding: '8px 16px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Plus style={{ width: '16px', height: '16px' }} />
                    Add Job
                  </button>

                  {historicalJobs.length > 0 && squareFeet > 0 && (() => {
                    const pricing = calculatePricePerSqFt();
                    return (
                      <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem' }}>Estimated Costs for {squareFeet} sqft:</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '13px' }}>
                          <div>
                            <div style={{ color: '#666' }}>Foundation:</div>
                            <div style={{ fontWeight: '600', color: '#1976D2' }}>{formatCurrency(squareFeet * pricing.foundation)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#666' }}>Framing:</div>
                            <div style={{ fontWeight: '600', color: '#1976D2' }}>{formatCurrency(squareFeet * pricing.framing)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#666' }}>Big C:</div>
                            <div style={{ fontWeight: '600', color: '#1976D2' }}>{formatCurrency(squareFeet * pricing.bigC)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowPricingModal(false)}
                      style={{ padding: '8px 16px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Close
                    </button>
                    {historicalJobs.length > 0 && squareFeet > 0 && (
                      <button
                        onClick={applyPricingToFoundation}
                        style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Apply Estimated Prices
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Concrete Pricing Modal */}
            {showConcretePricing && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '800px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  <h2 style={{ marginBottom: '1rem', fontSize: '20px', fontWeight: '600' }}>
                    C & D Concrete Pricing Options
                  </h2>
                  <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '14px' }}>
                    Manage your concrete pricing options and costs per unit.
                  </p>
                  
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '12px', textAlign: 'left', minWidth: '200px' }}>Option</th>
                          <th style={{ padding: '12px', textAlign: 'right', minWidth: '120px' }}>Cost</th>
                          <th style={{ padding: '12px', textAlign: 'left', minWidth: '100px' }}>Unit</th>
                          {/* <th style={{ padding: '12px', textAlign: 'center', width: '80px' }}>Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {concreteOptions.map((option) => (
                          <tr key={option.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="text"
                                value={option.option}
                                onChange={(e) => setConcreteOptions(prev => 
                                  prev.map(opt => opt.id === option.id ? { ...opt, option: e.target.value } : opt)
                                )}
                                onBlur={(e) => updateConcreteOption(option.id, 'option', e.target.value)}
                                placeholder="Option"
                                style={{ 
                                  width: '90%', 
                                  padding: '6px 10px', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '4px', 
                                  fontSize: '12px' 
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <div style={{ position: 'relative' }}>
                                <span style={{ 
                                  position: 'absolute', 
                                  left: '10px', 
                                  top: '50%', 
                                  transform: 'translateY(-50%)',
                                  color: '#666',
                                  fontSize: '12px'
                                }}>$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={option.costPerSqft}
                                  onChange={(e) => setConcreteOptions(prev => 
                                    prev.map(opt => opt.id === option.id ? { ...opt, costPerSqft: e.target.value } : opt)
                                  )}
                                  onBlur={(e) => updateConcreteOption(option.id, 'costPerSqft', e.target.value)}
                                  placeholder="0.00"
                                  style={{ 
                                    width: '90%', 
                                    padding: '6px 10px 6px 20px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px', 
                                    fontSize: '12px', 
                                    textAlign: 'right' 
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <select
                                value={option.unit}
                                onChange={(e) => updateConcreteOption(option.id, 'unit', e.target.value)}
                                style={{ 
                                  width: '90%', 
                                  padding: '6px 10px', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '4px', 
                                  fontSize: '12px' 
                                }}
                              >
                                <option value="sqft">per sqft</option>
                                <option value="each">each</option>
                                <option value="yard">per yard</option>
                                <option value="hour">per hour</option>
                                <option value="lnft">per lnft</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowConcretePricing(false)}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#666', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Concrete SQFT Modal */}
            {showConcreteSQFTPricing && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  <h2 style={{ marginBottom: '1rem', fontSize: '20px', fontWeight: '600' }}>
                    Concrete Pricing Calculator
                  </h2>
                  <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '14px' }}>
                    Enter square footage for each option and click Save to update your sworn statement.
                  </p>
                  
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '12px', textAlign: 'left', minWidth: '200px' }}>Option</th>
                          <th style={{ padding: '12px', textAlign: 'center', minWidth: '120px' }}>SQFT</th>
                          <th style={{ padding: '12px', textAlign: 'right', minWidth: '100px' }}>Cost/SQFT</th>
                          <th style={{ padding: '12px', textAlign: 'right', minWidth: '120px' }}>Total</th>
                          <th style={{ padding: '12px', textAlign: 'center', minWidth: '100px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {concreteSQFTOptions.length === 0 ? (
                          <tr>
                            <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                              Loading options...
                            </td>
                          </tr>
                        ) : (
                          concreteSQFTOptions.map((option) => {
                            const sqft = parseFloat(concreteSQFTInputs[option.id]) || 0;
                            const cost = parseFloat(option.costPerSqft) || 0;
                            const total = sqft * cost;
                            
                            // Normalize option name to match mapping
                            const optionName = (option.option || option.name || '').toLowerCase()
                              .replace(/"/g, '')
                              .replace(/'/g, '')
                              .replace(/ floor$/i, '')
                              .trim();
                            
                            // Map to expected item name
                            const itemMapping = {
                              'driveway': 'Driveway',
                              'garage floor': 'Garage Floor',
                              'garage': 'Garage Floor',
                              'basement floor': 'Basement',
                              'basement': 'Basement',
                              'porch 1-2 overhang': 'Front Porch',
                              'porch 1-2" overhang': 'Front Porch',
                              'porch 1-2 overhang': 'Front Porch',
                              'porch 1-2\' overhang': 'Front Porch',
                              'porch': 'Front Porch',
                              'front porch': 'Front Porch',
                              'back porch': 'Back Porch',
                              'sidewalk': 'Sidewalks',
                              'patio': 'Patio',
                              '4x4 service door': 'Service Door Pad',
                              '4x4x service door': 'Service Door Pad',
                              'service door': 'Service Door Pad',
                              '3ft steps': 'Steps',
                              'steps': 'Steps',
                              'slab': 'Slab',
                              'tile under sidewalk': 'Misc Concrete Expenses',
                              'east bob cat time': 'Misc Concrete Expenses',
                              'visqueen': 'Misc Concrete Expenses',
                              'extras': 'Misc Concrete Expenses',
                              'extras - winter charge, nc, calcium': 'Misc Concrete Expenses',
                              'extras - winter charge, nc, calcium (per yard not ft)': 'Misc Concrete Expenses'
                            };
                            
                            const mappedItemName = itemMapping[optionName];
                            
                            // Check if this item exists in sworn statement using the mapped name
                            const existingItem = mappedItemName ? existingConcreteItems[mappedItemName] : null;
                            const hasExistingValue = existingItem && existingItem.originalBidCost > 0;
                            
                            return (
                              <tr key={option.id} style={{ 
                                borderBottom: '1px solid #e9ecef',
                                backgroundColor: hasExistingValue ? '#f0f9ff' : 'transparent'
                              }}>
                                <td style={{ padding: '12px', fontWeight: '500' }}>
                                  {option.option || option.name}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <input
                                    type="number"
                                    value={concreteSQFTInputs[option.id] || ''}
                                    onChange={(e) => {
                                      setConcreteSQFTInputs(prev => ({
                                        ...prev,
                                        [option.id]: e.target.value
                                      }));
                                    }}
                                    placeholder="0"
                                    min="0"
                                    step="1"
                                    style={{ 
                                      width: '100px', 
                                      padding: '8px 12px', 
                                      border: hasExistingValue ? '2px solid #3b82f6' : '1px solid #ddd',
                                      borderRadius: '4px', 
                                      fontSize: '13px',
                                      textAlign: 'center',
                                      backgroundColor: hasExistingValue ? '#eff6ff' : 'white'
                                    }}
                                  />
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#666' }}>
                                  ${cost.toFixed(2)}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                                  ${total.toFixed(2)}
                                  {hasExistingValue && existingItem.originalBidCost !== total && total > 0 && (
                                    <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>
                                      Was: ${existingItem.originalBidCost.toFixed(2)}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  {(() => {
                                    if (hasExistingValue && existingItem.originalBidCost !== total) {
                                      return (
                                        <span style={{ 
                                          fontSize: '11px', 
                                          padding: '4px 8px', 
                                          backgroundColor: '#ff9800', 
                                          color: 'white', 
                                          borderRadius: '12px',
                                          fontWeight: '500'
                                        }}>
                                          Modified
                                        </span>
                                      );
                                    } else if (hasExistingValue) {
                                      return (
                                        <span style={{ 
                                          fontSize: '11px', 
                                          padding: '4px 8px', 
                                          backgroundColor: '#10b981', 
                                          color: 'white', 
                                          borderRadius: '12px',
                                          fontWeight: '500'
                                        }}>
                                          Saved
                                        </span>
                                      );
                                    } else if (sqft > 0) {
                                      return (
                                        <span style={{ 
                                          fontSize: '11px', 
                                          padding: '4px 8px', 
                                          backgroundColor: '#3b82f6', 
                                          color: 'white', 
                                          borderRadius: '12px',
                                          fontWeight: '500'
                                        }}>
                                          New
                                        </span>
                                      );
                                    } else {
                                      return (
                                        <span style={{ 
                                          fontSize: '11px', 
                                          padding: '4px 8px', 
                                          backgroundColor: '#e5e7eb', 
                                          color: '#6b7280', 
                                          borderRadius: '12px'
                                        }}>
                                          Empty
                                        </span>
                                      );
                                    }
                                  })()}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                          <td colSpan="3" style={{ padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>
                            Grand Total:
                          </td>
                          <td colSpan="2" style={{ padding: '12px', textAlign: 'right', fontWeight: '700', fontSize: '16px', color: '#2563eb' }}>
                            ${concreteSQFTOptions.reduce((sum, opt) => {
                              const sqft = parseFloat(concreteSQFTInputs[opt.id]) || 0;
                              const cost = parseFloat(opt.costPerSqft) || 0;
                              return sum + (sqft * cost);
                            }, 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowConcreteSQFTPricing(false);
                      }}
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: '#666', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Close
                    </button>
                    <button
                      onClick={saveAllConcreteItems}
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: '#4CAF50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Save style={{ width: '16px', height: '16px' }} />
                      Save All Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Overages Analysis Modal */}
            {showOveragesModal && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <OveragesAnalysis
                  user={user}
                  userRole={userRole}
                  onClose={() => setShowOveragesModal(false)}
                  onNavigate={() => setShowOveragesModal(false)}
                  currentProject={currentProject}
                  projects={projects}
                  onProjectChange={onProjectChange}
                />
              </div>
              
            )}

            {/* Add-Ons Modal */}
            {showAddOns && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '1200px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  <h2 style={{ marginBottom: '1rem', fontSize: '20px', fontWeight: '600' }}>ADD ONs/Overages</h2>
                  <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '14px' }}>
                    Track add-ons and overages with automatic profit calculations ({profitMarginPercent}% markup).
                  </p>
                  
                  {/* Add New Add-On Form */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    <input
                      ref={(input) => {
                        if (input) {
                          // Store reference but don't use controlled state
                          input.dataset.addOnName = input.value || '';
                        }
                      }}
                      type="text"
                      defaultValue=""
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const name = e.target.value.trim();
                          if (name) {
                            addAddOn(name);
                            e.target.value = ''; // Clear input
                          }
                        }
                      }}
                      placeholder="New add-on name..."
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        flex: 1
                      }}
                    />
                    <button
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        const name = input.value.trim();
                        if (name) {
                          addAddOn(name);
                          input.value = ''; // Clear input
                        }
                      }}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#4CAF50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      <Plus style={{ width: '14px', height: '14px' }} />
                      Add Item
                    </button>
                  </div>
                  
                  {/* Add-Ons Table */}
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '12px', textAlign: 'left', minWidth: '200px' }}>Item</th>
                          <th style={{ padding: '12px', textAlign: 'right', width: '120px' }}>Price</th>
                          <th style={{ padding: '12px', textAlign: 'right', width: '120px' }}>Overages</th>
                          <th style={{ padding: '12px', textAlign: 'right', width: '120px' }}>Price w/ {profitMarginPercent}%</th>
                          <th style={{ padding: '12px', textAlign: 'right', width: '120px' }}>RVCG Profit</th>
                          <th style={{ padding: '12px', textAlign: 'right', width: '120px' }}>Amount Paid</th>
                          <th style={{ padding: '12px', textAlign: 'right', width: '120px' }}>Still Due</th>
                          <th style={{ padding: '12px', textAlign: 'center', width: '80px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addOns.map((addon) => {
                          const price = parseFloat(addon.price) || 0;
                          const overages = parseFloat(addon.overages) || 0;
                          const priceWith25 = overages > 0 ? 0 : (price * (1 + profitMarginPercent / 100));
                          const rvcgProfit = overages > 0 ? 0 : (price * profitMarginPercent / 100);
                          
                          const amountPaid = parseFloat(addon.amountPaid) || 0;
                          const stillDue = overages > 0 ? overages - amountPaid : priceWith25 - amountPaid;
                          
                          return (
                            <tr key={addon.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '8px', fontWeight: '500' }}>
                                {addon.name}
                              </td>
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={addon.price || ''}
                                  onChange={(e) => updateAddOn(addon.id, 'price', e.target.value)}
                                  placeholder="0.00"
                                  style={{ 
                                    width: '90%', 
                                    border: '1px solid #ddd', 
                                    padding: '4px 8px', 
                                    fontSize: '12px',
                                    textAlign: 'right',
                                    borderRadius: '4px'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={addon.overages || ''}
                                  onChange={(e) => updateAddOn(addon.id, 'overages', e.target.value)}
                                  placeholder="0.00"
                                  style={{ 
                                    width: '90%', 
                                    border: '1px solid #ddd', 
                                    padding: '4px 8px', 
                                    fontSize: '12px',
                                    textAlign: 'right',
                                    borderRadius: '4px'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', backgroundColor: '#f8f9fa', fontWeight: '600' }}>
                                {formatCurrency(priceWith25)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', backgroundColor: '#e8f5e9', fontWeight: '600', color: '#4CAF50' }}>
                                {formatCurrency(rvcgProfit)}
                              </td>
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={addon.amountPaid || ''}
                                  onChange={(e) => updateAddOn(addon.id, 'amountPaid', e.target.value)}
                                  placeholder="0.00"
                                  style={{ 
                                    width: '100%', 
                                    border: '1px solid #ddd', 
                                    padding: '4px 8px', 
                                    fontSize: '12px',
                                    textAlign: 'right',
                                    borderRadius: '4px'
                                  }}
                                />
                              </td>
                              <td style={{ 
                                padding: '8px', 
                                textAlign: 'right', 
                                fontWeight: '600',
                                color: stillDue > 0 ? '#ff9800' : '#4CAF50'
                              }}>
                                {formatCurrency(stillDue)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <button
                                  onClick={() => deleteAddOn(addon.id)}
                                  style={{ 
                                    padding: '4px 8px', 
                                    backgroundColor: '#f44336', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    fontSize: '11px'
                                  }}
                                >
                                  <Trash2 style={{ width: '12px', height: '12px' }} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {/* Totals Row */}
                        {addOns.length > 0 && (
                          <tr style={{ backgroundColor: '#f8f9fa', fontWeight: '700', borderTop: '2px solid #dee2e6' }}>
                            <td style={{ padding: '12px' }}>Total</td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatCurrency(addOnTotals.price)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatCurrency(addOnTotals.overages)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e3f2fd', color: '#1976D2' }}>
                              {formatCurrency(addOnTotals.priceWith25)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e8f5e9', color: '#4CAF50' }}>
                              {formatCurrency(addOnTotals.rvcgProfit)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatCurrency(addOnTotals.amountPaid)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: addOnTotals.stillDue > 0 ? '#ff9800' : '#4CAF50' }}>
                              {formatCurrency(addOnTotals.stillDue)}
                            </td>
                            <td></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {addOns.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#999', padding: '2rem', fontSize: '14px' }}>
                      No add-ons yet. Add one above to get started.
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowAddOns(false)}
                      style={{ padding: '8px 16px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add Custom Item Modal */}
            {showAddItemModal && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '500px',
                  width: '100%'
                }}>
                  <h2 style={{ marginBottom: '1rem', fontSize: '20px', fontWeight: '600' }}>Add Custom Item</h2>
                  <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '14px' }}>
                    Add a custom line item to any category in the sworn statement.
                  </p>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
                      Category
                    </label>
                    <select
                      value={newItemCategory}
                      onChange={(e) => setNewItemCategory(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select a category...</option>
                      {Object.keys(CATEGORIES).map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
                      Item Name
                    </label>
                    <input
                      type="text"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addCustomItem();
                        }
                      }}
                      placeholder="Enter item name..."
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px', 
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowAddItemModal(false);
                        setNewItemCategory('');
                        setNewItemName('');
                      }}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#666', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addCustomItem}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#4CAF50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Project Info Section */}
            <div style={{ 
              backgroundColor: 'white', 
              padding: '1.5rem', 
              margin: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
                    Signed Contract Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={contractPrice}
                    onChange={(e) => setContractPrice(parseFloat(e.target.value) || 0)}
                    onBlur={saveProjectInfo}
                    className="modal-input"
                    style={{ width: '100%' }}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
                    Down Payment
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={downPayment}
                    onChange={(e) => setDownPayment(parseFloat(e.target.value) || 0)}
                    onBlur={saveProjectInfo}
                    className="modal-input"
                    style={{ width: '100%' }}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
                    Square Feet
                  </label>
                  <input
                    type="number"
                    value={squareFeet}
                    onChange={(e) => setSquareFeet(parseFloat(e.target.value) || 0)}
                    onBlur={saveProjectInfo}
                    className="modal-input"
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowPricingModal(true)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px 16px', backgroundColor: '#9C27B0', borderColor: '#9C27B0' }}
                >
                  <Ghost style={{ width: '16px', height: '16px' }} />
                  Historical Pricing
                </button>
                <button
                  onClick={() => setShowSummary(!showSummary)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px 16px' }}
                >
                  <Calculator style={{ width: '16px', height: '16px' }} />
                  {showSummary ? 'Hide' : 'Show'} Summary
                </button>
                <button 
                  onClick={() => setShowOveragesModal(true)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#14B8A6', borderColor: '#14B8A6' }}
                >
                  <TrendingUp style={{ width: '20px', height: '20px' }} />
                  View Overages Analysis
                </button>
                <button
                  onClick={() => setShowConcreteSQFTPricing(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#cde340ff',
                    color: 'black',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <DollarSign style={{ width: '16px', height: '16px' }} />
                  Concrete Pricing
                </button>
                {/* cabinet pricing */}
                <button 
                  onClick={() => setShowCalculator(true)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px 16px', backgroundColor: '#2d5016', borderColor: '#2d5016' }}
                >
                  <Wrench style={{ width: '16px', height: '16px' }} />
                  Cabinet Pricing
                </button>
                <button
                  onClick={() => setShowAddOns(true)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px 16px', backgroundColor: '#FF9800', borderColor: '#FF9800' }}
                >
                  <ClipboardPlus style={{ width: '16px', height: '16px' }} />
                  Add-Ons/Overages
                </button>
                <button
                  onClick={() => setShowAddItemModal(true)}
                  className="save-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px 16px', backgroundColor: '#673AB7', borderColor: '#673AB7' }}
                >
                  <Plus style={{ width: '16px', height: '16px' }} />
                  Add Custom Item
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            {showCalculator && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <CabinetCalculator 
                  onClose={() => setShowCalculator(false)} 
                  currentProject={currentProject}
                  swornData={swornData}
                  setSwornData={setSwornData}
                  savedCabinetQuantities={cabinetQuantities}
                  savedCabinetPricingType={cabinetPricingType}
                />
              </div>
            )}
            {showSummary && (
              <div style={{ 
                padding: '0 1.5rem 1.5rem 1.5rem',
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem' }}>Total Bid Costs</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{formatCurrency(totalBidCosts)}</div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem' }}>Running Contract Price</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{formatCurrency(runningContractPrice)}</div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem' }}>Total Draw Pulls</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{formatCurrency(drawReceivedTotal)}</div>
                  </div>
                  {/* Total Draw's Received Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem' }}>Total Draw Received</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{formatCurrency(drawsReceived.reduce((sum, val) => sum + (parseFloat(val) || 0), 0))}</div>
                  </div>
                  {/* Total Overages Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: totalOverages < 0 ? '2px solid #f44336' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#666', 
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {totalOverages < 0 && <AlertTriangle style={{ width: '14px', height: '14px', color: '#f44336' }} />}
                      Total Overages
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: totalOverages < 0 ? '#f44336' : '#4CAF50' 
                    }}>
                      {formatCurrency(totalOverages)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '0.25rem' }}>
                      Overages Percentage = {overagesPercent.toFixed(2)}%
                    </div>
                  </div>
                  {/* Total Under Budget Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: totalUnderBudget > 0 ? '2px solid #4CAF50' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#666', 
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {totalUnderBudget > 0 && <TrendingUp style={{ width: '14px', height: '14px', color: '#4CAF50' }} />}
                      Total Under Budget
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: totalUnderBudget > 0 ? '#4CAF50' : '#666' 
                    }}>
                      {formatCurrency(totalUnderBudget)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '0.25rem' }}>
                      Underages Percentage = {underagesPercent.toFixed(2)}%
                    </div>
                  </div>
                  {/* Total Potential Profit Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: totalPotentialProfit > 0 ? '2px solid #2196F3' : totalPotentialProfit < 0 ? '2px solid #f44336' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#666', 
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {totalPotentialProfit > 0 ? (
                        <TrendingUp style={{ width: '14px', height: '14px', color: '#2196F3' }} />
                      ) : totalPotentialProfit < 0 ? (
                        <AlertTriangle style={{ width: '14px', height: '14px', color: '#f44336' }} />
                      ) : null}
                      Total Potential Profit
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: totalPotentialProfit > 0 ? '#2196F3' : totalPotentialProfit < 0 ? '#f44336' : '#666'
                    }}>
                      {formatCurrency(totalPotentialProfit)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '0.25rem' }}>
                      Total Profit Percentage = {profitPercent.toFixed(2)}%
                    </div>
                  </div>
                  {/* Balance Towards Completion Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: totalTowardsCompletion < 0 ? '2px solid #f44336' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#666', 
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {totalTowardsCompletion < 0 && <AlertTriangle style={{ width: '14px', height: '14px', color: '#f44336' }} />}
                      Balance Towards Completion
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: totalTowardsCompletion < 0 ? '#f44336' : '#4CAF50' 
                    }}>
                      {formatCurrency(totalTowardsCompletion)}
                    </div>
                  </div>
                  {/* Current Projected Completion Total */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: projectedOverages < 0 ? '2px solid #f44336' : projectedOverages > 0 ? '2px solid #4CAF50' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#666', 
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {projectedOverages < 0 ? (
                        <AlertTriangle style={{ width: '14px', height: '14px', color: '#f44336' }} />
                      ) : projectedOverages > 0 ? (
                        <TrendingUp style={{ width: '14px', height: '14px', color: '#4CAF50' }} />
                      ) : null}
                      Current Projected Completion Total
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: projectedOverages < 0 ? '#f44336' : projectedOverages > 0 ? '#4CAF50' : '#666'
                    }}>
                      {formatCurrency(projectedOverages)}
                    </div>
                  </div>
                  {/* Price Per Sq Ft Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#666', 
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <Home style={{ width: '14px', height: '14px', color: '#9C27B0' }} />
                      Price Per Sq Ft
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: '#9C27B0'
                    }}>
                      {formatCurrency(costPerSqFt)}
                    </div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ fontSize: '11px', color: '#666' }}>Draw Pull</div>
                      <select 
                        value={selectedDraw}
                        onChange={(e) => setSelectedDraw(Number(e.target.value))}
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        {Array(8).fill(0).map((_, idx) => (
                          <option key={idx} value={idx}>Draw {idx + 1}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2196F3' }}>
                      {formatCurrency(drawPullTotal)}
                    </div>
                  </div>
                  {/* Draw Surplus Card */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ fontSize: '11px', color: '#666' }}> Draw Surplus</div>
                      
                      {/* Dropdown reused from Draw Pull */}
                      <select 
                        value={selectedDraw}
                        onChange={(e) => setSelectedDraw(Number(e.target.value))}
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        {Array(8).fill(0).map((_, idx) => (
                          <option key={idx} value={idx}>Draw {idx + 1}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: surplus >= 0 ? '#4CAF50' : '#F44336'
                    }}>
                      {formatCurrency(surplus)}
                    </div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: totalChangeOrders > 0 ? '2px solid #ff9800' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {totalChangeOrders > 0 && <AlertTriangle style={{ width: '14px', height: '14px', color: '#ff9800' }} />}
                      Total Change Orders
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: totalChangeOrders > 0 ? '#ff9800' : '#4CAF50' }}>
                      {formatCurrency(totalChangeOrders)}
                    </div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem' }}>% of Funds Pulled</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#9C27B0' }}>{percentagePulled}%</div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: fundsLeftToPull < 0 ? '2px solid #f44336' : '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem' }}>Funds Left to Pull</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: fundsLeftToPull < 0 ? '#f44336' : '#4CAF50' }}>
                      {formatCurrency(fundsLeftToPull)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            <div style={{ padding: '0 1.5rem 1.5rem 1.5rem', overflowX: 'auto' }}>
              <style>{`
                .sworn-table td:nth-child(odd), 
                .sworn-table th:nth-child(odd) {
                  background-color: #f5f5f5;
                }
                .sworn-table td:nth-child(even), 
                .sworn-table th:nth-child(even) {
                  background-color: #ffffff;
                }
                /* Override for header row */
                .sworn-table thead tr:first-child th {
                  background-color: #2d5016 !important;
                  color: white;
                }
                /* Override for specific styled cells */
                .sworn-table .change-orders-cell {
                  background-color: #e8f5e9 !important;
                }
                .sworn-table .draw-received-row {
                  background-color: #ADD8E6 !important;
                }
                .sworn-table .draw-received-row td {
                  background-color: #ADD8E6 !important;
                }
                .sworn-table .date-row {
                  background-color: #2196F3 !important;
                  color: white !important;
                }
                .sworn-table .date-row td {
                  background-color: #2196F3 !important;
                  color: white !important;
                }
                .sworn-table .category-header {
                  background-color: #f0f0f0 !important;
                }
                .sworn-table .category-total {
                  background-color: #f9f9f9 !important;
                }
                .sworn-table .grand-total {
                  background-color: #2d5016 !important;
                  color: white !important;
                }
                .sworn-table .grand-total td {
                  background-color: #2d5016 !important;
                  color: white !important;
                }
                /* Sticky first column */
                .sworn-table th:first-child,
                .sworn-table td:first-child {
                  position: sticky;
                  left: 0;
                  z-index: 10;
                  box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                }
                .sworn-table thead th:first-child {
                  z-index: 11;
                }
              `}</style>
              <div style={{ minWidth: '1400px' }}>
                <table className="sworn-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', fontWeight: '600', minWidth: '180px' }}>ITEM</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '90px' }}>Original Bid Cost</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '120px' }}>Contractor</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Complete</th>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <th key={num} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '70px' }}>DRAW {num}</th>
                      ))}
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '90px' }}>CHANGE ORDERS</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: '100px' }}>BALANCE TO COMPLETE</th>
                    </tr>
                    <tr>
                      {/* Empty cell under ITEM */}
                      <td></td>
                      {/* Empty cell under Original Bid Cost */}
                      <td></td>
                      {/* Empty cell under Contractor */}
                      <td></td>
                      {/* Empty cell under Complete */}
                      <td></td>
                      {/* One checkbox per Draw column */}
                      {[0, 1, 2, 3, 4, 5, 6, 7].map(drawIndex => (
                        <td key={drawIndex} style={{ textAlign: 'center', border: '1px solid #ddd' }}>
                          <input 
                            type="checkbox" 
                            checked={drawCheckboxes[drawIndex] || false}
                            onChange={(e) => updateDrawCheckbox(drawIndex, e.target.checked)}
                          />
                        </td>
                      ))}
                      {/* Empty under Change Orders */}
                      <td></td>
                      {/* Empty under Balance to Complete */}
                      <td></td>
                    </tr>
                    {/* Draw Received Row */}
                    <tr className="draw-received-row" style={{ fontWeight: '600' }}>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>Draw Received</td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((drawIndex) => (
                        <td key={drawIndex} style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={drawsReceived[drawIndex] || ''}
                            onChange={(e) => updateDrawReceived(drawIndex, e.target.value)}
                            placeholder="0.00"
                            style={{ 
                              width: '90%', 
                              border: '1px solid #ADD8E6', 
                              padding: '4px', 
                              fontSize: '11px',
                              textAlign: 'right',
                              borderRadius: '4px'
                            }}
                          />
                        </td>
                      ))}
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right', color: '#2196F3' }}>
                        {formatCurrency(drawsReceived.reduce((sum, val) => sum + (parseFloat(val) || 0), 0))}
                      </td>
                    </tr>
                    {/* Date Paid Row */}
                    <tr className="date-row" style={{fontWeight: '600' }}>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>Date</td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((dateIndex) => (
                        <td key={dateIndex} style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          <input
                            type="date"
                            value={datesReceived[dateIndex] || ''}
                            onChange={(e) => updateDatesReceived(dateIndex, e.target.value)}
                            style={{ 
                              width: '90%', 
                              border: '1px solid #e3f2fd', 
                              padding: '4px', 
                              fontSize: '11px',
                              textAlign: 'right',
                              borderRadius: '4px'
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(CATEGORIES).map(([category, data]) => (
                      <React.Fragment key={category}>
                        {/* Category Header */}
                        <tr className="category-header">
                          <td colSpan="14" style={{ padding: '6px 8px', border: '1px solid #ddd', fontWeight: '600', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{category}</span>
                            {(() => {
                              const totalOriginalBid = calculateCategoryTotal(category, 'originalBidCost');
                              
                              // Calculate total draws for this category
                              const totalDraws = [0, 1, 2, 3, 4, 5, 6, 7].reduce((sum, drawIndex) => {
                                return sum + calculateCategoryTotal(category, 'draw', drawIndex);
                              }, 0);
                              
                              // Don't show if no original bid
                              if (totalOriginalBid === 0 && totalDraws === 0) return null;
                              
                              const remaining = totalOriginalBid - totalDraws;
                              const percentageRemaining = ((remaining / totalOriginalBid) * 100);
                              const isOverBudget = totalDraws > totalOriginalBid;
                              
                              return (
                                <span style={{ 
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: isOverBudget ? '#dc3545' : '#28a745',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: isOverBudget ? '#ffe6e6' : '#e6f7e6'
                                }}>
                                  {isOverBudget ? '▲' : '▼'} {Math.abs(percentageRemaining).toFixed(2)}% {isOverBudget ? 'Over' : 'Under'} Budget
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                        
                        {/* Items */}
                        {(() => {
                          let items = category === 'Misc Expenses' ? miscExpenses.map(exp => exp.name) : data.items;
                          
                          // Add custom items for this category
                          if (customItems[category]) {
                            items = [...items, ...customItems[category]];
                          }
                          return items.map((item) => {
                            const itemData = getItemData(category, item);
                            const balance = calculateBalance(itemData, category, item);
                            const overage = parseFloat(itemData.changeOrders) || 0;
                            
                            return (
                            <tr key={item} style={{ backgroundColor: 'white' }}>
                              <td style={{ padding: '4px 8px', border: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{item}</span>
                                {(category === 'Misc Expenses' || (customItems[category] && customItems[category].includes(item))) && (
                                  <button
                                    onClick={() => {
                                      if (category === 'Misc Expenses') {
                                        const expense = miscExpenses.find(exp => exp.name === item);
                                        if (expense) deleteMiscExpense(expense.id);
                                      } else {
                                        deleteCustomItem(category, item);
                                      }
                                    }}
                                    style={{ 
                                      padding: '2px 6px', 
                                      backgroundColor: '#f44336', 
                                      color: 'white', 
                                      border: 'none', 
                                      borderRadius: '4px', 
                                      cursor: 'pointer',
                                      fontSize: '10px',
                                      marginLeft: '8px'
                                    }}
                                  >
                                    <Trash2 style={{ width: '12px', height: '12px' }} />
                                  </button>
                                )}
                              </td>
                              {/* Original Bid Cost */}
                              <td 
                                style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right', cursor: 'pointer' }}
                                onClick={() => handleCellClick(category, item, 'originalBidCost')}
                              >
                                {editingCell === `${category}|||${item}|||originalBidCost` ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleCellSave(category, item, 'originalBidCost')}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCellSave(category, item, 'originalBidCost')}
                                    autoFocus
                                    style={{ width: '100%', border: '1px solid #4CAF50', padding: '2px', fontSize: '11px' }}
                                  />
                                ) : (
                                  formatCurrency(itemData.originalBidCost)
                                )}
                              </td>
                              
                              {/* Contractor Name */}
                              <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                                <select
                                  value={itemData.contractorName || ''}
                                  onChange={(e) => saveItemData(category, item, 'contractorName', e.target.value)}
                                  style={{ 
                                    width: '100%', 
                                    border: '1px solid #ddd', 
                                    padding: '2px', 
                                    fontSize: '11px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="">Select Contractor</option>
                                  {contractors.map(contractor => (
                                    <option key={contractor.id} value={contractor.name}>
                                      {contractor.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              
                              {/* Job Complete Checkbox */}
                              <td style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={itemData.jobComplete || false}
                                  onChange={() => handleCellClick(category, item, 'jobComplete')}
                                  style={{ cursor: 'pointer' }}
                                />
                              </td>
                              
                              {/* Draw columns */}
                              {[0, 1, 2, 3, 4, 5, 6, 7].map((drawIndex) => (
                                <td 
                                  key={drawIndex}
                                  style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right', cursor: 'pointer' }}
                                  onClick={() => handleCellClick(category, item, 'draw', drawIndex)}
                                >
                                  {editingCell === `${category}|||${item}|||draw_${drawIndex}` ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={() => handleCellSave(category, item, 'draw', drawIndex)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleCellSave(category, item, 'draw', drawIndex)}
                                      autoFocus
                                      style={{ width: '100%', border: '1px solid #4CAF50', padding: '2px', fontSize: '11px' }}
                                    />
                                  ) : (
                                    formatCurrency(itemData.draws[drawIndex])
                                  )}
                                </td>
                              ))}
                              
                              {/* Change Orders */}
                              <td 
                                style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right', cursor: 'pointer', backgroundColor: '#e8f5e9' }}
                                onClick={() => handleCellClick(category, item, 'changeOrders')}
                              >
                                {editingCell === `${category}|||${item}|||changeOrders` ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleCellSave(category, item, 'changeOrders')}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCellSave(category, item, 'changeOrders')}
                                    autoFocus
                                    style={{ width: '100%', border: '1px solid #4CAF50', padding: '2px', fontSize: '11px' }}
                                  />
                                ) : (
                                  formatCurrency(itemData.changeOrders)
                                )}
                              </td>
                              
                              {/* Balance to Complete (Calculated) */}
                              <td style={{ padding: '4px 8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: '500' }}>
                                {formatCurrency(balance)}
                              </td>
                            </tr>)
                          });
                        })()}
                        
                        
                        {/* Category Total Row */}
                        <tr style={{ backgroundColor: '#f9f9f9', fontWeight: '600' }}>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>
                            {category === 'Misc Expenses' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>Total</span>
                                <input
                                  type="text"
                                  value={newMiscExpenseName}
                                  onChange={(e) => setNewMiscExpenseName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      addMiscExpense();
                                    }
                                  }}
                                  placeholder="New expense name..."
                                  style={{ 
                                    padding: '4px 8px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px', 
                                    fontSize: '11px',
                                    flex: 1
                                  }}
                                />
                                <button
                                  onClick={addMiscExpense}
                                  style={{ 
                                    padding: '4px 8px', 
                                    backgroundColor: '#4CAF50', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px'
                                  }}
                                >
                                  <Plus style={{ width: '12px', height: '12px' }} />
                                  Add
                                </button>
                              </div>
                            ) : (
                              'Total'
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                            {formatCurrency(calculateCategoryTotal(category, 'originalBidCost'))}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}></td>
                          {[0, 1, 2, 3, 4, 5, 6, 7].map((drawIndex) => (
                            <td key={drawIndex} style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                              {formatCurrency(calculateCategoryTotal(category, 'draw', drawIndex))}
                            </td>
                          ))}
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                            {formatCurrency(calculateCategoryTotal(category, 'changeOrders'))}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                            {formatCurrency(calculateCategoryTotal(category, 'balance'))}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                    
                    {/* Grand Total Row */}
                    <tr className="grand-total" style={{ fontWeight: '700', backgroundColor: '#2d5016', color: 'black' }}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>TOTAL BID COSTS</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                        {formatCurrency(totalBidCosts)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}></td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}></td>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((drawIndex) => (
                        <td key={drawIndex} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          {formatCurrency(calculateGrandTotal('draw', drawIndex))}
                        </td>
                      ))}
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', backgroundColor: '#4CAF50' }}>
                        {formatCurrency(totalChangeOrders)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                        {formatCurrency(calculateGrandTotal('balance'))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default SwornStatementsView;